import React, { useState, useCallback, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import PptxGenJS from 'pptxgenjs';
import { useAuth } from '../context/AuthContext';
import './SlideCreator.css';

// ── Undo/redo history ────────────────────────────────────────────────────────
// module-level clipboard — survives slide navigation
let _elementClipboard = null;

function useUndoState(initial) {
  const [state, rawSet] = useState(initial);
  const past = useRef([]);
  const future = useRef([]);
  const set = useCallback((updater) => {
    rawSet(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      past.current = [...past.current.slice(-30), prev];
      future.current = [];
      return next;
    });
  }, []);
  const undo = useCallback(() => {
    rawSet(prev => {
      if (!past.current.length) return prev;
      const restored = past.current[past.current.length - 1];
      past.current = past.current.slice(0, -1);
      future.current = [prev, ...future.current.slice(0, 29)];
      return restored;
    });
  }, []);
  const redo = useCallback(() => {
    rawSet(prev => {
      if (!future.current.length) return prev;
      const restored = future.current[0];
      future.current = future.current.slice(1);
      past.current = [...past.current.slice(-30), prev];
      return restored;
    });
  }, []);
  return [state, set, undo, redo];
}

// ── Themes ───────────────────────────────────────────────────────────────────
const THEMES = {
  // ── Classic / Corporate ──
  navy:     { id:'navy',     name:'Navy',      emoji:'🌊', brand:'#003B8E', accent:'#F5A623', dark:'#0D1B3E', light:'#EEF4FF', mid:'#1d4ed8' },
  sapphire: { id:'sapphire', name:'Sapphire',  emoji:'💎', brand:'#1E3A8A', accent:'#60A5FA', dark:'#0A1628', light:'#EFF6FF', mid:'#1D4ED8' },
  oxford:   { id:'oxford',   name:'Oxford',    emoji:'🏛️', brand:'#1A2F5A', accent:'#D4AF37', dark:'#0A1428', light:'#EFF6FF', mid:'#243B6E' },
  cobalt:   { id:'cobalt',   name:'Cobalt',    emoji:'⚡', brand:'#1D4ED8', accent:'#FDE047', dark:'#0A0F1E', light:'#EFF6FF', mid:'#2563EB' },
  slate:    { id:'slate',    name:'Slate',     emoji:'🩶', brand:'#334155', accent:'#38BDF8', dark:'#0F172A', light:'#F8FAFC', mid:'#475569' },
  graphite: { id:'graphite', name:'Graphite',  emoji:'🖤', brand:'#374151', accent:'#22D3EE', dark:'#111827', light:'#F9FAFB', mid:'#4B5563' },
  midnight: { id:'midnight', name:'Midnight',  emoji:'✨', brand:'#2D2D5E', accent:'#D4AF37', dark:'#09090F', light:'#FAFAF0', mid:'#4A4A8A' },
  // ── Academic ──
  maroon:   { id:'maroon',   name:'Maroon',    emoji:'🎓', brand:'#7F1D1D', accent:'#FBBF24', dark:'#300000', light:'#FEF3C7', mid:'#991B1B' },
  crimson:  { id:'crimson',  name:'Crimson',   emoji:'🔴', brand:'#991B1B', accent:'#FCA5A5', dark:'#450A0A', light:'#FEF2F2', mid:'#DC2626' },
  // ── Nature ──
  emerald:  { id:'emerald',  name:'Emerald',   emoji:'🌿', brand:'#065F46', accent:'#34D399', dark:'#022C22', light:'#ECFDF5', mid:'#047857' },
  forest:   { id:'forest',   name:'Forest',    emoji:'🌲', brand:'#14532D', accent:'#84CC16', dark:'#052E16', light:'#F7FEE7', mid:'#166534' },
  teal:     { id:'teal',     name:'Teal',      emoji:'🩵', brand:'#0F766E', accent:'#F472B6', dark:'#022B2B', light:'#F0FDFA', mid:'#0D9488' },
  ocean:    { id:'ocean',    name:'Ocean',     emoji:'🐋', brand:'#0369A1', accent:'#34D399', dark:'#082344', light:'#E0F2FE', mid:'#0284C7' },
  // ── Rich / Elegant ──
  violet:   { id:'violet',   name:'Violet',    emoji:'💜', brand:'#4C1D95', accent:'#A78BFA', dark:'#1E1B4B', light:'#EDE9FE', mid:'#6D28D9' },
  indigo:   { id:'indigo',   name:'Indigo',    emoji:'🌌', brand:'#3730A3', accent:'#FB7185', dark:'#14103A', light:'#EEF2FF', mid:'#4338CA' },
  plum:     { id:'plum',     name:'Plum',      emoji:'🍇', brand:'#6B21A8', accent:'#E879F9', dark:'#3B0764', light:'#FAF5FF', mid:'#7E22CE' },
  rose:     { id:'rose',     name:'Rose',      emoji:'🌹', brand:'#9D174D', accent:'#F43F5E', dark:'#500724', light:'#FFF1F2', mid:'#BE185D' },
  // ── Warm / Energetic ──
  sunset:   { id:'sunset',   name:'Sunset',    emoji:'🌅', brand:'#92400E', accent:'#FCD34D', dark:'#1C0A00', light:'#FFFBEB', mid:'#B45309' },
  mocha:    { id:'mocha',    name:'Mocha',     emoji:'☕', brand:'#4A2010', accent:'#FFB347', dark:'#1A0F08', light:'#FFF8F0', mid:'#6D3020' },
  dusk:     { id:'dusk',     name:'Dusk',      emoji:'🌆', brand:'#2D1E5C', accent:'#FF9F1C', dark:'#130E28', light:'#FFF4E6', mid:'#4B2E9E' },
  // ── Modern / Dark Mode ──
  aurora:   { id:'aurora',   name:'Aurora',    emoji:'🌠', brand:'#0D1F3C', accent:'#00E5FF', dark:'#050D1A', light:'#E0FDFF', mid:'#1A3A5C' },
  carbon:   { id:'carbon',   name:'Carbon',    emoji:'🌑', brand:'#1C1C1C', accent:'#F5C518', dark:'#080808', light:'#FFFBEB', mid:'#2E2E2E' },
  steel:    { id:'steel',    name:'Steel',     emoji:'🔩', brand:'#2E3A50', accent:'#FF6B35', dark:'#1A1F2E', light:'#FFF4F0', mid:'#3D4F66' },
  neon:     { id:'neon',     name:'Neon',      emoji:'⚡', brand:'#16004A', accent:'#00FFCC', dark:'#0A0014', light:'#EEFFF9', mid:'#2A005E' },
};

const THEME_GROUPS = [
  { label: '🏢 Classic & Corporate', ids: ['navy','sapphire','oxford','cobalt','slate','graphite','midnight'] },
  { label: '🎓 Academic',            ids: ['maroon','crimson'] },
  { label: '🌿 Nature',              ids: ['emerald','forest','teal','ocean'] },
  { label: '💎 Rich & Elegant',      ids: ['violet','indigo','plum','rose'] },
  { label: '🌅 Warm & Energetic',    ids: ['sunset','mocha','dusk'] },
  { label: '🌑 Modern & Dark Mode',  ids: ['aurora','carbon','steel','neon'] },
];

// ── Custom theme builder ──────────────────────────────────────────────────────
function hexToRgb(hex) {
  return [1,3,5].map(i => parseInt(hex.slice(i,i+2),16));
}
function darkenHex(hex, f) {
  return '#'+hexToRgb(hex).map(c=>Math.max(0,Math.round(c*f)).toString(16).padStart(2,'0')).join('');
}
function lightenHex(hex, f) {
  return '#'+hexToRgb(hex).map(c=>Math.min(255,Math.round(c+(255-c)*f)).toString(16).padStart(2,'0')).join('');
}
function buildCustomTheme(brand, accent) {
  return { id:'custom', name:'Custom', emoji:'🎨', brand, accent,
    dark: darkenHex(brand, 0.38), light: lightenHex(brand, 0.88), mid: brand };
}

const SLIDE_TYPES = {
  concept:  { label: '📝 Concept',  desc: 'Heading + Definition + Key Points' },
  blank:    { label: '⬜ Blank',    desc: 'Blank canvas with footer + teacher safe box' },
};

const defaultSlides = () => [
  { id: Date.now(),   type: 'concept', heading: '', elements: [], backgroundImage: '', backgroundOpacity: 0.3, bgColor: '' },
  { id: Date.now()+1, type: 'blank',   elements: [], backgroundImage: '', backgroundOpacity: 0.3, bgColor: '' },
];

const TEACHER_NAME = 'Nipun Sir (B.Tech, IIT Bombay)';
const BRAND_COPY = '© 2026 PrrepX Pathshala';
const WEBSITE_URL = 'www.prrepxpathshala.live';
const EMPTY_SLIDE_HINTS = {
  cover: 'Edit the lecture title, topic, and chapter details for the cover slide.',
  summary: 'Use the summary slide as a clean closing page for your lecture.',
  default: 'Use the toolbar to add text, question blocks, shapes, or images.',
};

// ── Inline editable span ──────────────────────────────────────────────────────
function EditableSpan({ value, onChange, className, style, tag: Tag = 'span', placeholder }) {
  const ref = useRef(null);
  const focused = useRef(false);
  // useLayoutEffect avoids flash: sets innerHTML before browser paint
  useLayoutEffect(() => {
    if (ref.current && !focused.current && ref.current.innerHTML !== (value || '')) {
      ref.current.innerHTML = value || '';
    }
  }, [value]);
  return (
    <Tag
      ref={ref}
      className={`${className || ''}${onChange ? ' sp-editable' : ''}`}
      style={style}
      contentEditable={!!onChange}
      suppressContentEditableWarning
      onFocus={() => { focused.current = true; }}
      onBlur={onChange ? e => { focused.current = false; onChange(e.currentTarget.innerHTML); } : undefined}
      data-ph={placeholder || ''}
    />
  );
}

function HardTextWrap({ hkey, selKey, onSelect, children }) {
  const isSel = selKey === hkey && !!onSelect;
  return (
    <div className={`sp-ht-wrap${isSel ? ' sp-ht-wrap--sel' : ''}`}
      onClick={onSelect ? e => { e.stopPropagation(); onSelect(hkey); } : undefined}>
      {children}
    </div>
  );
}

// ── Shared footer used by all slide types ─────────────────────────────────────
function SlideFooter({ info, t, dark, onInfoUpdate }) {
  return (
    <div className="sp-footer" style={{ background: dark ? '#1a0a00' : t.brand }}>
      {onInfoUpdate
        ? <EditableSpan className="sp-footer-teacher" style={{ color: '#E5E7EB' }} value={info.teacherName || TEACHER_NAME} onChange={v => onInfoUpdate({ teacherName: v })} />
        : <span className="sp-footer-teacher" style={{ color: '#E5E7EB' }}>{info.teacherName || TEACHER_NAME}</span>
      }
      {onInfoUpdate
        ? <EditableSpan className="sp-footer-brand" style={{ color: t.accent }} value={info.brandCopy || BRAND_COPY} onChange={v => onInfoUpdate({ brandCopy: v })} />
        : <span className="sp-footer-brand" style={{ color: t.accent }}>{info.brandCopy || BRAND_COPY}</span>
      }
    </div>
  );
}

// ── Slide Canvas (free-form element editor) ────────────────────────────────────
// ── contentEditable editor for canvas text elements ─────────────────────────
function CanvasTextEditor({ content, style, onSave, onStop }) {
  const ref = useRef(null);
  useLayoutEffect(() => {
    if (!ref.current) return;
    ref.current.innerHTML = content || '';
    ref.current.focus();
    const range = document.createRange();
    range.selectNodeContents(ref.current);
    range.collapse(false);
    window.getSelection()?.removeAllRanges();
    window.getSelection()?.addRange(range);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div ref={ref} contentEditable suppressContentEditableWarning className="sc-elem-ce" style={style}
      onBlur={e => {
        // focus moved into sbar (e.g. size select) — keep edit mode alive
        if (e.relatedTarget?.closest?.('.sc-elem-sbar')) return;
        onSave(e.currentTarget.innerHTML);
        onStop();
      }}
      onClick={e => e.stopPropagation()} onMouseDown={e => e.stopPropagation()} />
  );
}

function SlideCanvas({ elements = [], onAddElement, onUpdateElement, onRemoveElement, onClickEmpty, onDuplicateElement, onApplyToAll, overlay }) {
  const readOnly = !onAddElement;
  const [selectedId, setSelectedId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [hasSelection, setHasSelection] = useState(false);
  const containerRef = useRef(null);
  const dragRef = useRef(null);
  const rotateRef = useRef(null);
  const hideSelTimer = useRef(null);
  const savedCERange = useRef(null);

  // track text selection inside contentEditable elements
  useEffect(() => {
    const onSel = () => {
      clearTimeout(hideSelTimer.current);
      const sel = window.getSelection();
      const inCE = !!(sel && !sel.isCollapsed && sel.anchorNode?.parentElement?.closest('.sc-elem-ce'));
      if (inCE) {
        savedCERange.current = sel.getRangeAt(0).cloneRange();
        setHasSelection(true);
      } else {
        // delay hide so select onChange can fire before sbar disappears
        hideSelTimer.current = setTimeout(() => setHasSelection(false), 300);
      }
    };
    document.addEventListener('selectionchange', onSel);
    return () => { document.removeEventListener('selectionchange', onSel); clearTimeout(hideSelTimer.current); };
  }, []);

  const applyInlineSize = px => {
    if (!savedCERange.current) return;
    const anchor = savedCERange.current.startContainer;
    const ceEl = (anchor.nodeType === Node.TEXT_NODE ? anchor.parentElement : anchor)?.closest?.('.sc-elem-ce');
    // restore saved range in case focus left the editor when clicking the dropdown
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(savedCERange.current);
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    if (range.collapsed) return;
    const span = document.createElement('span');
    span.style.fontSize = px + 'px';
    try { range.surroundContents(span); }
    catch {
      document.execCommand('fontSize', false, '7');
      document.querySelectorAll('.sc-elem-ce font[size="7"]').forEach(f => {
        const s = document.createElement('span');
        s.style.fontSize = px + 'px';
        while (f.firstChild) s.appendChild(f.firstChild);
        f.parentNode?.replaceChild(s, f);
      });
    }
    // refocus editor to cancel its blur timer
    if (ceEl) { const r = savedCERange.current.cloneRange(); ceEl.focus(); sel?.removeAllRanges(); sel?.addRange(r); }
  };

  useEffect(() => {
    const onMove = e => {
      if (!dragRef.current) return;
      const { id, mode, sx, sy, ox, oy, ow, oh, cw, ch } = dragRef.current;
      const dx = ((e.clientX - sx) / cw) * 100;
      const dy = ((e.clientY - sy) / ch) * 100;
      if (mode === 'move') {
        onUpdateElement?.(id, { x: Math.max(0, Math.min(90, ox + dx)), y: Math.max(0, Math.min(88, oy + dy)) });
      } else {
        onUpdateElement?.(id, { w: Math.max(8, Math.min(98, ow + dx)), h: Math.max(5, Math.min(90, oh + dy)) });
      }
    };
    const onMoveRotate = e => {
      if (!rotateRef.current) return;
      const { id, cx, cy } = rotateRef.current;
      const angle = Math.round(Math.atan2(e.clientY - cy, e.clientX - cx) * 180 / Math.PI);
      onUpdateElement?.(id, { rotate: angle });
    };
    const onUp = () => { dragRef.current = null; rotateRef.current = null; };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mousemove', onMoveRotate);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mousemove', onMoveRotate);
      window.removeEventListener('mouseup', onUp);
    };
  }, [onUpdateElement]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onPaste = e => {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (const item of items) {
        if (item.type.startsWith('image/')) {
          const reader = new FileReader();
          reader.onload = ev => onAddElement?.('image', ev.target.result);
          reader.readAsDataURL(item.getAsFile());
          e.preventDefault();
          return;
        }
      }
    };
    el.addEventListener('paste', onPaste);
    return () => el.removeEventListener('paste', onPaste);
  }, [onAddElement]);

  // keyboard shortcuts for selected element
  useEffect(() => {
    if (readOnly) return;
    const canvas = containerRef.current;
    if (!canvas) return;
    const onKeyDown = e => {
      if (editingId) return;
      // paste works without a selected element
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'v') {
        if (_elementClipboard && onAddElement) { e.preventDefault(); onAddElement('clone', _elementClipboard); }
        return;
      }
      if (!selectedId) return;
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'c') {
        const elem = elements.find(el => el.id === selectedId);
        if (elem) { e.preventDefault(); _elementClipboard = { ...elem }; }
        return;
      }
      if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        onRemoveElement?.(selectedId); setSelectedId(null); setEditingId(null);
      } else if (e.key === 'Escape') {
        setSelectedId(null); setEditingId(null);
      } else if (e.key.startsWith('Arrow')) {
        e.preventDefault();
        const elem = elements.find(el => el.id === selectedId);
        if (!elem) return;
        const step = e.shiftKey ? 5 : 1;
        if (e.key === 'ArrowLeft')  onUpdateElement?.(selectedId, { x: Math.max(0, elem.x - step) });
        if (e.key === 'ArrowRight') onUpdateElement?.(selectedId, { x: Math.min(90, elem.x + step) });
        if (e.key === 'ArrowUp')    onUpdateElement?.(selectedId, { y: Math.max(0, elem.y - step) });
        if (e.key === 'ArrowDown')  onUpdateElement?.(selectedId, { y: Math.min(88, elem.y + step) });
      } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'd') {
        e.preventDefault(); onDuplicateElement?.(selectedId);
      }
    };
    canvas.addEventListener('keydown', onKeyDown);
    return () => canvas.removeEventListener('keydown', onKeyDown);
  }, [selectedId, editingId, elements, onRemoveElement, onUpdateElement, onDuplicateElement, readOnly]);

  const startDrag = (e, el, mode) => {
    if (editingId) return;
    e.stopPropagation(); e.preventDefault();
    setSelectedId(el.id);
    const rect = containerRef.current.getBoundingClientRect();
    dragRef.current = { id: el.id, mode, sx: e.clientX, sy: e.clientY, ox: el.x, oy: el.y, ow: el.w, oh: el.h, cw: rect.width, ch: rect.height };
  };

  const startRotate = (e, el) => {
    e.stopPropagation(); e.preventDefault();
    const canvasRect = containerRef.current.getBoundingClientRect();
    const cx = canvasRect.left + (el.x + el.w / 2) * canvasRect.width / 100;
    const cy = canvasRect.top + (el.y + el.h / 2) * canvasRect.height / 100;
    rotateRef.current = { id: el.id, cx, cy };
  };

  // single-click on empty canvas → deselect
  const handleCanvasSingleClick = e => {
    if (e.target.closest('.sc-elem') || e.target.closest('[contenteditable]') || e.target.closest('.sc-rotate-handle')) return;
    setSelectedId(null); setEditingId(null);
  };

  // double-click on empty canvas → create text element at cursor
  const handleCanvasDoubleClick = e => {
    if (e.target.closest('.sc-elem') || e.target.closest('[contenteditable]') || e.target.closest('.sc-rotate-handle')) return;
    setSelectedId(null); setEditingId(null);
    if (!onClickEmpty) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = Math.min(Math.round(((e.clientX - rect.left) / rect.width) * 100), 75);
    const y = Math.min(Math.round(((e.clientY - rect.top) / rect.height) * 100), 80);
    const id = onClickEmpty(x, y);
    if (id) { setSelectedId(id); setEditingId(id); }
  };

  return (
    <div className={`sc-canvas${overlay ? ' sc-canvas--overlay' : ''}`} ref={containerRef} tabIndex={overlay ? -1 : 0}
      onClick={overlay ? undefined : handleCanvasSingleClick}
      onDoubleClick={overlay ? undefined : handleCanvasDoubleClick}>

      {/* placed elements */}
      {elements.map(el => {
        const isSel = selectedId === el.id;
        const isEdit = editingId === el.id;
        // flip sbar above→below when near top, left→right when near right wall
        const sbarPos = {
          ...(el.y < 14 ? { top: '105%', bottom: 'auto' } : {}),
          ...((el.x + el.w) > 58 ? { left: 'auto', right: 0 } : {}),
        };
        return (
          <div key={el.id}
            className={`sc-elem sc-elem--${el.type}${isSel ? ' sc-elem--sel' : ''}`}
            style={{ left:`${el.x}%`, top:`${el.y}%`, width:`${el.w}%`, height:`${el.h}%`, ...(el.bgColor && el.type !== 'image' && el.type !== 'shape' ? { background: el.bgColor, borderRadius: '4px' } : {}) }}
            onClick={e => {
              e.stopPropagation();
              setSelectedId(el.id);
              if (selectedId !== el.id) setEditingId(null);
            }}
            onMouseDown={e => { if (e.target.classList.contains('sc-elem-resize')) return; startDrag(e, el, 'move'); }}
            onDoubleClick={e => { e.stopPropagation(); if (el.type !== 'image' && el.type !== 'shape') setEditingId(el.id); }}>

            {el.type === 'image'
              ? <img src={el.content} alt="" draggable={false}
                  style={{ width:'100%', height:'100%', objectFit:'contain', display:'block', pointerEvents:'none' }} />
              : el.type === 'shape'
                ? (
                  <div className={`sc-shape sc-shape--${el.shapeKind || 'rect'}`}
                    style={{ transform: `rotate(${el.rotate || 0}deg)`, transformOrigin: 'center' }}>
                    {(el.shapeKind === 'line' || el.shapeKind === 'dotted') ? (
                      <div
                        className="sc-shape-line"
                        style={{
                          borderTopColor: el.stroke || '#111111',
                          borderTopWidth: `${el.strokeWidth || 2}px`,
                          borderTopStyle: el.shapeKind === 'dotted' ? 'dotted' : 'solid',
                        }}
                      />
                    ) : (
                      <div
                        className="sc-shape-fill"
                        style={{
                          background: el.fill || '#dbeafe',
                          borderColor: el.stroke || '#1e40af',
                          borderWidth: `${el.strokeWidth || 2}px`,
                          borderStyle: 'solid',
                          borderRadius: el.shapeKind === 'circle' ? '50%' : '6px',
                        }}
                      />
                    )}
                    {isSel && (
                      <div
                        className={`sc-rotate-handle${(el.shapeKind === 'line' || el.shapeKind === 'dotted') ? ' sc-rotate-handle--line' : ''}`}
                        onMouseDown={e => startRotate(e, el)}
                        title="Drag to rotate"
                      />
                    )}
                  </div>
                )
              : isEdit
                ? <CanvasTextEditor
                    content={el.content}
                    style={{ textAlign: el.align || 'left' }}
                    onSave={html => onUpdateElement?.(el.id, { content: html })}
                    onStop={() => setEditingId(null)} />
                : <div className="sc-elem-txt"
                    style={{ fontSize:`${el.fontSize||13}px`, color: el.color||(el.type==='question'?'#5B21B6':'#111'), fontWeight: el.bold?700:400, fontStyle: el.italic ? 'italic' : 'normal', textDecoration: el.underline ? 'underline' : 'none', textAlign: el.align || 'left' }}
                    dangerouslySetInnerHTML={{ __html: el.content || `<em style="opacity:.38;font-style:italic">${el.type==='question'?'Double-click to type question…':'Double-click to type…'}</em>` }} />
            }

            {/* keep mounted during entire edit session so native select dropdown survives hasSelection=false */}
            {el.id === editingId && (
              <div className="sc-elem-sbar" style={{ ...sbarPos, ...(hasSelection ? {} : { visibility: 'hidden', pointerEvents: 'none' }) }} onClick={e=>e.stopPropagation()} onMouseDown={e => { e.preventDefault(); e.stopPropagation(); }}>
                <select className="sc-sbar-fsz" defaultValue="" title="Font size"
                  onMouseDown={e => e.stopPropagation()}
                  onChange={e => { if (e.target.value) { applyInlineSize(+e.target.value); e.target.value = ''; } }}>
                  <option value="" disabled>Size</option>
                  {[8,9,10,11,12,14,16,18,20,22,24,28,32,36,40,48,56,64,72,96].map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <div className="sc-sbar-divider" />
                <button className="sc-sbar-b" style={{ fontWeight: 900 }} title="Bold" onClick={() => document.execCommand('bold')}>B</button>
                <button className="sc-sbar-b" style={{ fontStyle: 'italic' }} title="Italic" onClick={() => document.execCommand('italic')}>I</button>
                <button className="sc-sbar-b" style={{ textDecoration: 'underline' }} title="Underline" onClick={() => document.execCommand('underline')}>U</button>
                <div className="sc-sbar-divider" />
                <label className="sc-sbar-color" title="Text color">
                  <span>A</span>
                  <input type="color" defaultValue="#111111" onInput={e => document.execCommand('foreColor', false, e.target.value)} />
                </label>
                <div className="sc-sbar-divider" />
                <label className="sc-sbar-color" title="Box background">
                  <span>Bg</span>
                  <input type="color" value={el.bgColor || '#fffde7'} onChange={e => onUpdateElement?.(el.id, { bgColor: e.target.value })} />
                </label>
                {el.bgColor && <button className="sc-sbar-b" style={{fontSize:'10px',padding:'0 4px'}} title="Remove background" onClick={() => onUpdateElement?.(el.id, { bgColor: '' })}>✕Bg</button>}
                <div className="sc-sbar-divider" />
                <button className="sc-sbar-b sc-sbar-b--del" title="Delete element" onClick={() => { onRemoveElement?.(el.id); setSelectedId(null); setEditingId(null); }}>✕</button>
              </div>
            )}

            {isSel && el.type === 'image' && (
              <div className="sc-elem-sbar" style={sbarPos} onClick={e=>e.stopPropagation()} onMouseDown={e=>e.stopPropagation()}>
                {onApplyToAll && <button className="sc-sbar-b sc-sbar-b--apply" title="Copy to all slides" onClick={() => onApplyToAll(el.id)}>→All</button>}
                <button className="sc-sbar-b sc-sbar-b--del" title="Delete" onClick={() => { onRemoveElement?.(el.id); setSelectedId(null); setEditingId(null); }}>✕ Del</button>
              </div>
            )}

            {isSel && el.type === 'shape' && (
              <div className="sc-elem-sbar" style={sbarPos} onClick={e=>e.stopPropagation()} onMouseDown={e=>e.stopPropagation()}>
                <select
                  value={el.shapeKind || 'rect'}
                  onChange={e => onUpdateElement?.(el.id, { shapeKind: e.target.value })}
                >
                  <option value="rect">Rect</option>
                  <option value="circle">Circle</option>
                  <option value="line">Line</option>
                  <option value="dotted">Dotted</option>
                </select>
                <label className="sc-sbar-color">
                  <span>S</span>
                  <input type="color" value={el.stroke || '#111111'} onChange={e => onUpdateElement?.(el.id, { stroke: e.target.value })} />
                </label>
                {(el.shapeKind !== 'line' && el.shapeKind !== 'dotted') && (
                  <label className="sc-sbar-color">
                    <span>F</span>
                    <input type="color" value={el.fill || '#dbeafe'} onChange={e => onUpdateElement?.(el.id, { fill: e.target.value })} />
                  </label>
                )}
                <input
                  type="number"
                  value={el.strokeWidth || 2}
                  min={1}
                  max={12}
                  title="Stroke width"
                  onChange={e => onUpdateElement?.(el.id, { strokeWidth: +e.target.value })}
                />
                {onApplyToAll && <button className="sc-sbar-b sc-sbar-b--apply" title="Copy to all slides" onClick={() => onApplyToAll(el.id)}>→All</button>}
                <button className="sc-sbar-b sc-sbar-b--del" title="Delete" onClick={() => { onRemoveElement?.(el.id); setSelectedId(null); setEditingId(null); }}>✕</button>
              </div>
            )}

            {isSel && <div className="sc-elem-resize" onMouseDown={e=>{ e.stopPropagation(); startDrag(e, el, 'resize'); }} />}
          </div>
        );
      })}
    </div>
  );
}

// ── Selection-based inline format toolbar ────────────────────────────────────
function SelectionToolbar() {
  const [bar, setBar] = useState({ visible: false, x: 0, y: 0 });
  const savedRange = useRef(null);
  const hideTimer = useRef(null);

  useEffect(() => {
    const onSel = () => {
      clearTimeout(hideTimer.current);
      const sel = window.getSelection();
      if (!sel || sel.isCollapsed || sel.rangeCount === 0) {
        hideTimer.current = setTimeout(() => setBar(b => ({ ...b, visible: false })), 250);
        return;
      }
      const anchor = sel.anchorNode;
      // only activate for hardcoded slide texts, not canvas elements (those have their own sbar)
      const inEditable = anchor?.parentElement?.closest('[contenteditable="true"]');
      if (!inEditable || inEditable.classList.contains('sc-elem-ce')) {
        setBar(b => ({ ...b, visible: false }));
        return;
      }
      const range = sel.getRangeAt(0);
      savedRange.current = range.cloneRange();
      const rect = range.getBoundingClientRect();
      if (!rect.width) return;
      setBar({ visible: true, x: rect.left + rect.width / 2, y: rect.top });
    };
    document.addEventListener('selectionchange', onSel);
    return () => { document.removeEventListener('selectionchange', onSel); clearTimeout(hideTimer.current); };
  }, []);

  const restore = () => {
    if (!savedRange.current) return;
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(savedRange.current);
  };

  const exec = (cmd, val) => { restore(); document.execCommand(cmd, false, val ?? null); };

  const applySize = px => {
    restore();
    const range = window.getSelection()?.getRangeAt(0);
    if (!range || range.collapsed) return;
    const span = document.createElement('span');
    span.style.fontSize = px + 'px';
    try { range.surroundContents(span); }
    catch {
      document.execCommand('fontSize', false, '7');
      document.querySelectorAll('[contenteditable] font[size="7"]').forEach(f => {
        const s = document.createElement('span');
        s.style.fontSize = px + 'px';
        while (f.firstChild) s.appendChild(f.firstChild);
        f.parentNode?.replaceChild(s, f);
      });
    }
  };

  if (!bar.visible) return null;
  return (
    <div className="sc-elem-sbar"
      style={{ position: 'fixed', left: bar.x, top: bar.y, transform: 'translate(-50%, calc(-100% - 8px))', zIndex: 9999 }}
      onMouseDown={e => e.preventDefault()}>
      <select className="sc-sbar-fsz" defaultValue="" title="Font size"
        onMouseDown={e => e.stopPropagation()}
        onChange={e => { if (e.target.value) { applySize(+e.target.value); e.target.value = ''; } }}>
        <option value="" disabled>Size</option>
        {[8,9,10,11,12,14,16,18,20,22,24,28,32,36,40,48,56,64,72,96].map(s => <option key={s} value={s}>{s}</option>)}
      </select>
      <div className="sc-sbar-divider" />
      <button className="sc-sbar-b" style={{ fontWeight: 900 }} onClick={() => exec('bold')}>B</button>
      <button className="sc-sbar-b" style={{ fontStyle: 'italic' }} onClick={() => exec('italic')}>I</button>
      <button className="sc-sbar-b" style={{ textDecoration: 'underline' }} onClick={() => exec('underline')}>U</button>
      <div className="sc-sbar-divider" />
      <label className="sc-sbar-color" title="Text color" onMouseDown={e => e.preventDefault()}>
        <span>A</span>
        <input type="color" defaultValue="#ffffff" onInput={e => exec('foreColor', e.target.value)} />
      </label>
    </div>
  );
}

// PPT wide layout is 13.33 x 7.5 inches. Keep preview box in the same ratio as PPT export.
const TEACHER_BOX = { xPct: 0, yPct: 62.67, wPct: 19.5, hPct: 37.33 };

function SlideBackground({ image, opacity = 0.3 }) {
  if (!image) return null;
  return <div className="sp-bg-image" style={{ backgroundImage: `url(${image})`, opacity }} />;
}

function TeacherSafeBox({ show, color, onDelete, onColorChange }) {
  if (show === false) return null;
  return (
    <div className="sp-teacher-box" style={{ left: `${TEACHER_BOX.xPct}%`, top: `${TEACHER_BOX.yPct}%`, width: `${TEACHER_BOX.wPct}%`, height: `${TEACHER_BOX.hPct}%`, background: color || '#000000' }}>
      {(onDelete || onColorChange) && (
        <div className="sp-teacher-box-actions">
          {onColorChange && <input type="color" className="sp-teacher-box-color" value={color || '#000000'} onChange={e => onColorChange(e.target.value)} title="Box color" />}
          {onDelete && <button className="sp-overlay-del" onClick={onDelete} title="Remove box">×</button>}
        </div>
      )}
    </div>
  );
}

function TopRightLogo({ show, onDelete }) {
  if (show === false) return null;
  return (
    <div className="sp-top-right-logo-wrap">
      <img src="/PrepXPathshala_logo2.jpg" alt="logo" className="sp-top-right-logo" />
      {onDelete && <button className="sp-overlay-del sp-logo-del" onClick={onDelete} title="Remove logo">×</button>}
    </div>
  );
}

// ── Live Preview ──────────────────────────────────────────────────────────────
function SlidePreview({ slide, info, themeObj, slideNum, totalSlides, onInfoUpdate, onUpdate, onUpdatePoint, onAddElement, onUpdateElement, onRemoveElement, onClickEmpty, onDuplicateElement, onApplyToAll, specialBackgrounds }) {
  const t = themeObj;
  const bgForCover = specialBackgrounds?.cover || { image: '', opacity: 0.3 };
  const bgForSummary = specialBackgrounds?.summary || { image: '', opacity: 0.3 };
  const slideRef = useRef(null);

  const makeDraggable = useCallback((posKey, defaultPos) => {
    if (!onInfoUpdate) return {};
    return {
      onMouseDown: e => {
        if (e.button !== 0 || e.target.tagName === 'BUTTON' || e.target.tagName === 'INPUT' || e.target.closest?.('[contenteditable]')) return;
        e.preventDefault(); e.stopPropagation();
        const rect = slideRef.current?.getBoundingClientRect();
        if (!rect) return;
        const cur = info[posKey] ?? defaultPos;
        const start = { mx: e.clientX, my: e.clientY, x: cur.x, y: cur.y };
        const onMove = me => {
          const dx = (me.clientX - start.mx) / rect.width * 100;
          const dy = (me.clientY - start.my) / rect.height * 100;
          onInfoUpdate({ [posKey]: { ...cur, x: Math.max(0, Math.min(92, start.x + dx)), y: Math.max(0, Math.min(95, start.y + dy)) } });
        };
        const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onInfoUpdate, info]);

  const [selHardkey, setSelHardkey] = useState(null);
  const hs = key => info.styles?.[key] || {};
  const htStyle = (key, def) => {
    const s = hs(key);
    return { ...def, ...(s.color && { color: s.color }), ...(s.fontSize && { fontSize: `${s.fontSize}px` }), ...(s.bold != null && { fontWeight: s.bold ? 'bold' : 'normal' }), ...(s.italic != null && { fontStyle: s.italic ? 'italic' : 'normal' }) };
  };
  const setInfoStyle = (key, patch) => {
    if (!onInfoUpdate) return;
    onInfoUpdate({ styles: { ...(info.styles || {}), [key]: { ...(info.styles?.[key] || {}), ...patch } } });
  };
  const resetInfoStyle = (key) => {
    if (!onInfoUpdate) return;
    const styles = { ...(info.styles || {}) };
    delete styles[key];
    onInfoUpdate({ styles });
  };
  const ht = hkey => ({ hkey, selKey: selHardkey, onSelect: onInfoUpdate ? setSelHardkey : null });
  const makeHardSbar = () => {
    if (!selHardkey || !onInfoUpdate) return null;
    const s = hs(selHardkey);
    return (
      <div className="sp-ht-sbar" onMouseDown={e => e.stopPropagation()} onClick={e => e.stopPropagation()}>
        <input type="number" className="sp-ht-sbar-fs" placeholder="px" value={s.fontSize || ''}
          onChange={e => setInfoStyle(selHardkey, { fontSize: e.target.value ? Number(e.target.value) : null })} />
        <div className="sc-sbar-divider" />
        <button className={`sc-sbar-b${s.bold ? ' active' : ''}`}
          onMouseDown={e => e.stopPropagation()}
          onClick={e => { e.stopPropagation(); setInfoStyle(selHardkey, { bold: !s.bold }); }}>B</button>
        <button className={`sc-sbar-b${s.italic ? ' active' : ''}`} style={{ fontStyle: 'italic' }}
          onMouseDown={e => e.stopPropagation()}
          onClick={e => { e.stopPropagation(); setInfoStyle(selHardkey, { italic: !s.italic }); }}>I</button>
        <div className="sc-sbar-divider" />
        <label className="sc-sbar-color" title="Text color">
          <span>A</span>
          <input type="color" value={s.color || '#ffffff'} onChange={e => setInfoStyle(selHardkey, { color: e.target.value })} />
        </label>
        <div className="sc-sbar-divider" />
        <button className="sc-sbar-b" title="Reset style" onClick={e => { e.stopPropagation(); resetInfoStyle(selHardkey); }}>↺</button>
        <button className="sc-sbar-b sc-sbar-b--del" title="Dismiss" onClick={e => { e.stopPropagation(); setSelHardkey(null); }}>×</button>
      </div>
    );
  };

  const coverBoxPos = info.coverBoxPos ?? { x: 12, y: 14, w: 76 };
  const coverSubtitlePos = info.coverSubtitlePos ?? { x: 8, y: 62, w: 84 };
  const logoPos = info.logoPos ?? { x: 86, y: 0 };
  const teacherPos = info.teacherPos ?? { x: 82, y: 64, w: 18, h: 36 };
  const fNamePos = info.footerNamePos ?? { x: 60, y: 93.5, w: 20 };
  const fBrandPos = info.footerBrandPos ?? { x: 2.5, y: 93.5, w: 47 };
  const fWebPos = info.footerWebsitePos ?? { x: 33, y: 93.5, w: 23 };

  const logo = (
    <div className="sp-top-right-logo-wrap sp-draggable" title="Drag to move"
      style={{ left: `${logoPos.x}%`, top: `${logoPos.y}%`, right: 'auto', width: '14%' }}
      {...makeDraggable('logoPos', { x: 86, y: 0 })}>
      <img src="/PrepXPathshala_logo2.jpg" alt="logo" className="sp-top-right-logo" />
    </div>
  );

  const teacherBox = info.showTeacherBox !== false ? (
    <div className="sp-teacher-box sp-draggable" title="Drag to move"
      style={{ left: `${teacherPos.x}%`, top: `${teacherPos.y}%`, width: `${teacherPos.w ?? 20}%`, height: `${teacherPos.h ?? 37}%`, background: info.teacherBoxColor || '#000000' }}
      {...makeDraggable('teacherPos', { x: 82, y: 64, w: 18, h: 36 })}>
      {onInfoUpdate && (
        <div className="sp-teacher-box-actions">
          <input type="color" className="sp-teacher-box-color" value={info.teacherBoxColor || '#000000'} onChange={e => onInfoUpdate({ teacherBoxColor: e.target.value })} title="Box color" />
          <button className="sp-overlay-del" onClick={e => { e.stopPropagation(); onInfoUpdate({ showTeacherBox: false }); }} title="Remove box">×</button>
        </div>
      )}
    </div>
  ) : null;

  const makeFooter = (dark = false) => (
    <>
      <div className="sp-footer-bar" style={{ background: dark ? '#1a0a00' : t.brand }} />
      <div className="sp-draggable sp-footer-text" title="Drag to move"
        style={{ left: `${fBrandPos.x}%`, top: `${fBrandPos.y}%`, width: `${fBrandPos.w ?? 47}%` }}
        {...makeDraggable('footerBrandPos', { x: 2.5, y: 93.5, w: 47 })}>
        <HardTextWrap {...ht('footerBrand')}>
          <EditableSpan value={info.brandCopy || BRAND_COPY} onChange={v => onInfoUpdate?.({ brandCopy: v })} className="sp-footer-brand" style={htStyle('footerBrand', { color: t.accent })} />
        </HardTextWrap>
      </div>
      <div className="sp-draggable sp-footer-text" title="Drag to move"
        style={{ left: `${fWebPos.x}%`, top: `${fWebPos.y}%`, width: `${fWebPos.w ?? 23}%` }}
        {...makeDraggable('footerWebsitePos', { x: 33, y: 93.5, w: 23 })}>
        <HardTextWrap {...ht('footerWebsite')}>
          <EditableSpan value={info.footerWebsite || WEBSITE_URL} onChange={v => onInfoUpdate?.({ footerWebsite: v })} className="sp-footer-brand" style={htStyle('footerWebsite', { color: t.accent })} />
        </HardTextWrap>
      </div>
      <div className="sp-draggable sp-footer-text" title="Drag to move"
        style={{ left: `${fNamePos.x}%`, top: `${fNamePos.y}%`, width: `${fNamePos.w ?? 20}%` }}
        {...makeDraggable('footerNamePos', { x: 60, y: 93.5, w: 20 })}>
        <HardTextWrap {...ht('footerName')}>
          <EditableSpan value={info.teacherName || TEACHER_NAME} onChange={v => onInfoUpdate?.({ teacherName: v })} className="sp-footer-teacher" style={htStyle('footerName', { color: '#E5E7EB' })} />
        </HardTextWrap>
      </div>
    </>
  );

  if (slide === 'cover') {
    return (
      <div ref={slideRef} className="sp-slide" style={{ background: bgForCover.bgColor || t.dark }} onClick={() => setSelHardkey(null)}>
        {makeHardSbar()}
        <SlideBackground image={bgForCover.image} opacity={bgForCover.opacity} />
        <div className="sp-cover-bg-overlay" />
        {logo}
        <div className="sp-cover-stripe" style={{ background: t.accent }} />
        <div className="sp-cover-corner" style={{ borderColor: t.accent }} />
        <div className="sp-cover-box sp-draggable"
          style={{ left: `${coverBoxPos.x}%`, top: `${coverBoxPos.y}%`, width: `${coverBoxPos.w}%` }}
          {...makeDraggable('coverBoxPos', { x: 12, y: 10, w: 72 })}>
          <div className="sp-cover-num-wrap" style={{ background: t.accent }}>
            <HardTextWrap {...ht('coverNum')}>
              <EditableSpan tag="div" className="sp-cover-num"
                style={htStyle('coverNum', { color: t.dark })}
                value={info.coverNum != null ? info.coverNum : `Lecture ${info.num}`}
                onChange={v => onInfoUpdate?.({ coverNum: v })} placeholder="Lecture 1" />
            </HardTextWrap>
          </div>
          <HardTextWrap {...ht('coverTopic')}>
            <EditableSpan tag="div" className="sp-cover-topic"
              style={htStyle('coverTopic', { color: '#fff' })}
              value={info.coverTopicLine != null ? info.coverTopicLine : (info.topic || '')}
              onChange={v => onInfoUpdate?.({ coverTopicLine: v })} placeholder="Enter topic name…" />
          </HardTextWrap>
          <HardTextWrap {...ht('coverChapter')}>
            <EditableSpan tag="div" className="sp-cover-chapter"
              style={htStyle('coverChapter', { color: 'rgba(200,220,255,0.7)' })}
              value={info.coverChapterLine != null ? info.coverChapterLine : (info.chapter ? `Chapter: ${info.chapter}` : '')}
              onChange={v => onInfoUpdate?.({ coverChapterLine: v })} placeholder="Chapter: Enter chapter…" />
          </HardTextWrap>
          <div className="sp-cover-box-divider" />
          <HardTextWrap {...ht('coverSubtitle')}>
            <EditableSpan tag="div" className="sp-cover-subtitle-text"
              style={htStyle('coverSubtitle', { color: t.accent })}
              value={info.coverSubtitle ?? 'Complete Physics for JEE/NEET & Class 11-12th'}
              onChange={v => onInfoUpdate?.({ coverSubtitle: v })}
              placeholder="Complete Physics for JEE/NEET…" />
          </HardTextWrap>
        </div>
        {teacherBox}
        {makeFooter(true)}
        <SlideCanvas overlay elements={info.coverElements||[]} onAddElement={onAddElement} onUpdateElement={onUpdateElement} onRemoveElement={onRemoveElement} onDuplicateElement={onDuplicateElement} />
      </div>
    );
  }

  if (slide === 'summary') {
    return (
      <div ref={slideRef} className="sp-slide" style={{ background: bgForSummary.bgColor || t.dark }} onClick={() => setSelHardkey(null)}>
        {makeHardSbar()}
        <SlideBackground image={bgForSummary.image} opacity={bgForSummary.opacity} />
        {logo}
        <div className="sp-content">
          <HardTextWrap {...ht('summaryTitle')}>
            <EditableSpan tag="h2" className="sp-heading" style={htStyle('summaryTitle', { color: '#fff' })}
              value={info.summaryTitle || '✅ Summary'} onChange={v => onInfoUpdate?.({ summaryTitle: v })} placeholder="Summary…" />
          </HardTextWrap>
          <div className="sp-rule" style={{ background: t.accent }} />
        </div>
        {teacherBox}
        {makeFooter(true)}
        <SlideCanvas overlay elements={info.summaryElements||[]} onAddElement={onAddElement} onUpdateElement={onUpdateElement} onRemoveElement={onRemoveElement} onDuplicateElement={onDuplicateElement} />
      </div>
    );
  }

  if (slide.type === 'concept') {
    return (
      <div ref={slideRef} className="sp-slide sp-slide--white" style={{ background: slide.bgColor || '#ffffff' }} onClick={() => setSelHardkey(null)}>
        {makeHardSbar()}
        <SlideBackground image={slide.backgroundImage} opacity={slide.backgroundOpacity ?? 0.3} />
        {logo}
        <div className="sp-content">
          <EditableSpan tag="h2" className="sp-heading" style={{ color: t.brand, display: 'block' }}
            value={slide.heading} onChange={v => onUpdate?.({ heading: v })} placeholder="Heading…" />
          <div className="sp-rule" style={{ background: t.accent }} />
          <SlideCanvas elements={slide.elements||[]} onAddElement={onAddElement} onUpdateElement={onUpdateElement} onRemoveElement={onRemoveElement} onClickEmpty={onClickEmpty} onDuplicateElement={onDuplicateElement} onApplyToAll={onApplyToAll} />
        </div>
        {teacherBox}
        {makeFooter()}
      </div>
    );
  }

  if (slide.type === 'blank') {
    return (
      <div ref={slideRef} className="sp-slide sp-slide--white" style={{ background: slide.bgColor || '#ffffff' }} onClick={() => setSelHardkey(null)}>
        {makeHardSbar()}
        <SlideBackground image={slide.backgroundImage} opacity={slide.backgroundOpacity ?? 0.3} />
        {logo}
        <div className="sp-content">
          <SlideCanvas elements={slide.elements||[]} onAddElement={onAddElement} onUpdateElement={onUpdateElement} onRemoveElement={onRemoveElement} onClickEmpty={onClickEmpty} onDuplicateElement={onDuplicateElement} onApplyToAll={onApplyToAll} />
        </div>
        {teacherBox}
        {makeFooter()}
      </div>
    );
  }

  return null;
}

// ── Slide Thumbnail (scaled-down live preview for the slide rail) ─────────────
const THUMB_RENDER_W = 780;
const THUMB_SCALE = 0.2; // 156px display = 780px × 0.2

function SlideThumbnail({ slide, info, themeObj, specialBackgrounds }) {
  return (
    <div className="sc-thumb">
      <div
        className="sc-thumb__inner"
        style={{
          width: THUMB_RENDER_W,
          height: Math.round(THUMB_RENDER_W * 9 / 16),
          transform: `scale(${THUMB_SCALE})`,
          transformOrigin: 'top left',
        }}
      >
        <SlidePreview
          slide={slide}
          info={info}
          themeObj={themeObj}
          specialBackgrounds={specialBackgrounds}
        />
      </div>
    </div>
  );
}

// ── Graph Picker ────────────────────────────────────────────────────────────────────
const GRAPH_CATS = ['Algebra','Trig','Inv Trig','Exp/Log','Special','Blank'];
const GRAPH_DEFS = [
  { id:'linear', cat:'Algebra',  name:'y = x',       fn:x=>x,                                             xR:[-4,4],     yR:[-4,4] },
  { id:'quad',   cat:'Algebra',  name:'y = x²',      fn:x=>x*x,                                           xR:[-3,3],     yR:[-0.5,9] },
  { id:'quad_n', cat:'Algebra',  name:'y = −x²',     fn:x=>-x*x,                                          xR:[-3,3],     yR:[-9,0.5] },
  { id:'cubic',  cat:'Algebra',  name:'y = x³',      fn:x=>x**3,                                          xR:[-2.5,2.5], yR:[-8,8] },
  { id:'abs',    cat:'Algebra',  name:'y = |x|',     fn:x=>Math.abs(x),                                   xR:[-4,4],     yR:[-0.3,4] },
  { id:'sqrt',   cat:'Algebra',  name:'y = √x',      fn:x=>x>=0?Math.sqrt(x):NaN,                         xR:[-0.5,5],   yR:[-0.3,3] },
  { id:'cbrt',   cat:'Algebra',  name:'y = ∛x',      fn:x=>Math.cbrt(x),                                  xR:[-4,4],     yR:[-2,2] },
  { id:'recip',  cat:'Algebra',  name:'y = 1/x',     fn:x=>Math.abs(x)>0.05?1/x:NaN,                     xR:[-4,4],     yR:[-4,4] },
  { id:'recip2', cat:'Algebra',  name:'y = 1/x²',    fn:x=>Math.abs(x)>0.05?1/(x*x):NaN,                 xR:[-4,4],     yR:[-0.3,6] },
  { id:'x23',    cat:'Algebra',  name:'y = x^(2/3)', fn:x=>Math.cbrt(x*x),                                xR:[-4,4],     yR:[-0.3,3] },
  { id:'sin',    cat:'Trig',     name:'y = sin x',   fn:x=>Math.sin(x),                                   xR:[-6.5,6.5], yR:[-1.4,1.4] },
  { id:'cos',    cat:'Trig',     name:'y = cos x',   fn:x=>Math.cos(x),                                   xR:[-6.5,6.5], yR:[-1.4,1.4] },
  { id:'tan',    cat:'Trig',     name:'y = tan x',   fn:x=>{const t=Math.tan(x);return Math.abs(t)<5?t:NaN;}, xR:[-5,5],  yR:[-4,4] },
  { id:'sin2x',  cat:'Trig',     name:'y = sin 2x',  fn:x=>Math.sin(2*x),                                 xR:[-4,4],     yR:[-1.4,1.4] },
  { id:'sin2',   cat:'Trig',     name:'y = sin²x',   fn:x=>Math.sin(x)**2,                                xR:[-6.5,6.5], yR:[-0.1,1.2] },
  { id:'cos2',   cat:'Trig',     name:'y = cos²x',   fn:x=>Math.cos(x)**2,                                xR:[-6.5,6.5], yR:[-0.1,1.2] },
  { id:'abss',   cat:'Trig',     name:'y = |sin x|', fn:x=>Math.abs(Math.sin(x)),                         xR:[-6.5,6.5], yR:[-0.1,1.2] },
  { id:'absc',   cat:'Trig',     name:'y = |cos x|', fn:x=>Math.abs(Math.cos(x)),                         xR:[-6.5,6.5], yR:[-0.1,1.2] },
  { id:'asin',   cat:'Inv Trig', name:'y = sin⁻¹x',  fn:x=>Math.abs(x)<=1?Math.asin(x):NaN,              xR:[-1.3,1.3], yR:[-1.8,1.8] },
  { id:'acos',   cat:'Inv Trig', name:'y = cos⁻¹x',  fn:x=>Math.abs(x)<=1?Math.acos(x):NaN,              xR:[-1.3,1.3], yR:[-0.2,3.5] },
  { id:'atan',   cat:'Inv Trig', name:'y = tan⁻¹x',  fn:x=>Math.atan(x),                                  xR:[-7,7],     yR:[-1.8,1.8] },
  { id:'acot',   cat:'Inv Trig', name:'y = cot⁻¹x',  fn:x=>Math.PI/2-Math.atan(x),                        xR:[-7,7],     yR:[-0.2,3.5] },
  { id:'ex',     cat:'Exp/Log',  name:'y = eˣ',       fn:x=>Math.exp(x),                                   xR:[-3,3],     yR:[-0.3,9] },
  { id:'enx',    cat:'Exp/Log',  name:'y = e⁻ˣ',      fn:x=>Math.exp(-x),                                  xR:[-3,3],     yR:[-0.3,9] },
  { id:'lnx',    cat:'Exp/Log',  name:'y = ln x',     fn:x=>x>0.01?Math.log(x):NaN,                        xR:[-0.5,7],   yR:[-3,3] },
  { id:'log10',  cat:'Exp/Log',  name:'y = log₁₀x',   fn:x=>x>0.01?Math.log10(x):NaN,                     xR:[-0.5,7],   yR:[-1.5,1.5] },
  { id:'floor',  cat:'Special',  name:'y = ⌊x⌋',      fn:x=>Math.floor(x),                                 xR:[-3.5,3.5], yR:[-3.5,3.5], skip:0.5 },
  { id:'frac',   cat:'Special',  name:'y = {x}',      fn:x=>x-Math.floor(x),                               xR:[-3,3],     yR:[-0.1,1.1], skip:0.5 },
  { id:'sgn',    cat:'Special',  name:'y = sgn(x)',   fn:x=>x===0?0:x>0?1:-1,                              xR:[-3,3],     yR:[-1.5,1.5], skip:1 },
  { id:'xabs',   cat:'Special',  name:'y = x·|x|',    fn:x=>x*Math.abs(x),                                 xR:[-3,3],     yR:[-9,9] },
  // Blank axes templates — fn always NaN so no curve is plotted
  { id:'blk_1q',  cat:'Blank', name:'1st Quad',        fn:()=>NaN, xR:[-0.3,5],  yR:[-0.3,5],  noName:true },
  { id:'blk_1qc', cat:'Blank', name:'1st Quad Clean',  fn:()=>NaN, xR:[-0.3,5],  yR:[-0.3,5],  noLabel:true, noName:true },
  { id:'blk_4q',  cat:'Blank', name:'4 Quadrants',     fn:()=>NaN, xR:[-5,5],    yR:[-5,5],    noName:true },
  { id:'blk_4qc', cat:'Blank', name:'4 Quad Clean',    fn:()=>NaN, xR:[-5,5],    yR:[-5,5],    noLabel:true, noName:true },
];

function renderGraph(def, size = 400) {
  const cvs = document.createElement('canvas');
  const S = size; cvs.width = S; cvs.height = S;
  const ctx = cvs.getContext('2d');
  const P = Math.round(S * 0.12), gw = S-2*P, gh = S-2*P;
  ctx.fillStyle = '#fff'; ctx.fillRect(0,0,S,S);
  const [x1,x2] = def.xR, [y1,y2] = def.yR;
  const mx = x => P + (x-x1)/(x2-x1)*gw;
  const my = y => P + (1-(y-y1)/(y2-y1))*gh;
  // grid
  ctx.strokeStyle='#e8ecf0'; ctx.lineWidth=0.8;
  for (let i=0; i<=4; i++) {
    const gx=x1+(x2-x1)*i/4, gy=y1+(y2-y1)*i/4;
    ctx.beginPath(); ctx.moveTo(mx(gx),P); ctx.lineTo(mx(gx),P+gh); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(P,my(gy)); ctx.lineTo(P+gw,my(gy)); ctx.stroke();
  }
  // axes
  ctx.strokeStyle='#555'; ctx.lineWidth=1.5;
  const hasXax=y1<0&&y2>0, hasYax=x1<0&&x2>0;
  const fs = Math.round(S*0.05);
  if (hasXax) {
    const y0=my(0); ctx.beginPath(); ctx.moveTo(P,y0); ctx.lineTo(P+gw,y0); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(P+gw,y0); ctx.lineTo(P+gw-7,y0-3.5); ctx.lineTo(P+gw-7,y0+3.5); ctx.closePath(); ctx.fillStyle='#555'; ctx.fill();
    if (!def.noLabel) { ctx.fillStyle='#555'; ctx.font=`${fs}px sans-serif`; ctx.textAlign='left'; ctx.textBaseline='middle'; ctx.fillText('x',P+gw+4,y0); }
  }
  if (hasYax) {
    const x0=mx(0); ctx.beginPath(); ctx.moveTo(x0,P+gh); ctx.lineTo(x0,P); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(x0,P); ctx.lineTo(x0-3.5,P+7); ctx.lineTo(x0+3.5,P+7); ctx.closePath(); ctx.fillStyle='#555'; ctx.fill();
    if (!def.noLabel) { ctx.fillStyle='#555'; ctx.font=`${fs}px sans-serif`; ctx.textAlign='center'; ctx.textBaseline='bottom'; ctx.fillText('y',x0,P-2); }
  }
  if (hasXax&&hasYax&&!def.noLabel) { ctx.fillStyle='#888'; ctx.font=`${Math.round(S*.046)}px sans-serif`; ctx.textAlign='right'; ctx.textBaseline='top'; ctx.fillText('O',mx(0)-3,my(0)+3); }
  // curve
  ctx.strokeStyle='#1d4ed8'; ctx.lineWidth=Math.max(2,S/150); ctx.lineJoin='round'; ctx.lineCap='round';
  ctx.beginPath();
  let started=false, prevY=NaN;
  const margin=(y2-y1)*0.02;
  for (let i=0; i<=2000; i++) {
    const x=x1+(x2-x1)*i/2000, y=def.fn(x);
    if (!isFinite(y)||isNaN(y)||y<y1-margin||y>y2+margin) { started=false; prevY=NaN; continue; }
    if (def.skip&&isFinite(prevY)&&Math.abs(y-prevY)>def.skip) started=false;
    const px=mx(x), py=my(y);
    if (!started) { ctx.moveTo(px,py); started=true; } else ctx.lineTo(px,py);
    prevY=y;
  }
  ctx.stroke();
  // name label
  if (!def.noName) { ctx.fillStyle='#1e293b'; ctx.font=`bold ${Math.round(S*.062)}px sans-serif`; ctx.textAlign='center'; ctx.textBaseline='bottom'; ctx.fillText(def.name, S/2, S-3); }
  return cvs.toDataURL('image/png');
}

// ── HTML → pptxgenjs text run helpers ───────────────────────────────────────
const _parseColor = str => {
  if (!str) return null;
  str = str.trim();
  if (/^#[0-9A-Fa-f]{6}$/.test(str)) return str.slice(1).toUpperCase();
  if (/^#[0-9A-Fa-f]{3}$/.test(str)) return str.slice(1).split('').map(c=>c+c).join('').toUpperCase();
  const m = str.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
  if (m) return [+m[1],+m[2],+m[3]].map(v=>v.toString(16).padStart(2,'0')).join('').toUpperCase();
  return null;
};

const htmlToTextRuns = (html, baseOpts = {}) => {
  if (!html) return '';
  if (!html.includes('<')) return html;
  const div = document.createElement('div');
  div.innerHTML = html;
  const runs = [];
  const walk = (node, opts) => {
    if (node.nodeType === 3) {
      if (node.textContent) runs.push({ text: node.textContent, options: { ...opts } });
      return;
    }
    if (node.nodeType !== 1) return;
    const tag = node.tagName.toLowerCase();
    if (tag === 'br') { runs.push({ text: '\n', options: { ...opts } }); return; }
    const o = { ...opts };
    if (tag === 'b' || tag === 'strong') o.bold = true;
    if (tag === 'i' || tag === 'em') o.italic = true;
    if (tag === 'u') o.underline = { type: 'sng' };
    if (tag === 'font') { const c = _parseColor(node.getAttribute('color')); if (c) o.color = c; }
    const st = node.style;
    if (st) {
      if (st.fontWeight === 'bold' || +st.fontWeight >= 700) o.bold = true;
      if (st.fontStyle === 'italic') o.italic = true;
      if (st.textDecoration?.includes('underline')) o.underline = { type: 'sng' };
      const c = _parseColor(st.color); if (c) o.color = c;
      if (st.fontSize) { const px = parseFloat(st.fontSize); if (!isNaN(px)) o.fontSize = Math.max(6, Math.round(px * 72/84)); }
    }
    const isBlock = ['div','p','li'].includes(tag);
    if (isBlock && runs.length) runs.push({ text: '\n', options: { ...opts } });
    for (const child of node.childNodes) walk(child, o);
  };
  walk(div, baseOpts);
  while (runs.length && runs[runs.length-1].text === '\n') runs.pop();
  if (!runs.length) return '';
  if (runs.length === 1) return runs[0].text;
  return runs;
};

// strips all HTML and returns plain text (for fields where .toUpperCase() etc. is needed)
const stripHtml = html => {
  const r = htmlToTextRuns(html);
  if (typeof r === 'string') return r;
  return r.map(x => x.text).join('');
};

// ── PPT generator ─────────────────────────────────────────────────────────────
async function generatePPT({ info, themeObj, slides, includeSummary, specialBackgrounds }) {
  const t = themeObj;
  const pres = new PptxGenJS();
  pres.layout = 'LAYOUT_WIDE';

  const LOGO_URL = window.location.origin + '/PrepXPathshala_logo2.jpg';
  let logoData = null;
  try {
    const resp = await fetch(LOGO_URL);
    const blob = await resp.blob();
    logoData = await new Promise(res => { const r = new FileReader(); r.onload = ev => res(ev.target.result); r.readAsDataURL(blob); });
  } catch(e) {}
  const coverBg = specialBackgrounds?.cover || { image: '', opacity: 0.3 };
  const summaryBg = specialBackgrounds?.summary || { image: '', opacity: 0.3 };
  // pt = px × 72/84 — preview renders at 1120px wide = 84dpi, not CSS 96dpi reference
  const _pptSt = (key, defs) => { const s = info.styles?.[key] || {}; return { fontSize: s.fontSize ? Math.round(s.fontSize * 72/84) : defs.fontSize, color: (s.color||defs.color||'#000000').replace('#',''), bold: s.bold!=null?s.bold:(defs.bold||false), italic: s.italic!=null?s.italic:false }; };

  const addTeacherBlock = (slide) => {
    if (info.showTeacherBox === false) return;
    const tc = (info.teacherBoxColor || '#000000').replace('#', '');
    const tp = info.teacherPos ?? { x: 82, y: 64, w: 18, h: 36 };
    slide.addShape(pres.ShapeType.rect, { x: tp.x/100*13.33, y: tp.y/100*7.5, w: (tp.w??20)/100*13.33, h: (tp.h??37)/100*7.5, fill:{ color: tc }, line:{ color: tc, pt:0 } });
  };

  const addBackgroundImage = (slide, imageData, opacity = 0.3) => {
    if (!imageData) return;
    try {
      slide.addImage({ data: imageData, x:0, y:0, w:13.33, h:7.5, sizing:{ type:'cover' } });
      // Fade the background image using a white overlay so content remains readable.
      slide.addShape(pres.ShapeType.rect, {
        x:0, y:0, w:13.33, h:7.5,
        fill:{ color:'FFFFFF', transparency: Math.max(0, Math.min(100, Math.round((1 - opacity) * 100))) },
        line:{ color:'FFFFFF', transparency:100, pt:0 },
      });
    } catch(e) {}
  };

  const addFooter = (slide, dark) => {
    const fnp = info.footerNamePos ?? { x: 60, y: 93.5, w: 20 };
    const fbp = info.footerBrandPos ?? { x: 2.5, y: 93.5, w: 47 };
    const fwp = info.footerWebsitePos ?? { x: 33, y: 93.5, w: 23 };
    const _fn = _pptSt('footerName', { fontSize:14, color:'#E5E7EB' }); // CSS sp-footer-teacher clamp max 16px → 14pt
    const _fb = _pptSt('footerBrand', { fontSize:15, color:t.accent, bold:true }); // CSS sp-footer-brand clamp max 17px → 15pt
    slide.addShape(pres.ShapeType.rect, { x:0, y:6.95, w:13.33, h:0.55, fill:{ color: dark ? '1a0a00' : t.brand.replace('#','') } });
    slide.addText(htmlToTextRuns(info.brandCopy || BRAND_COPY, { fontSize:_fb.fontSize, color:_fb.color, bold:_fb.bold, italic:_fb.italic }), { x: fbp.x/100*13.33, y: fbp.y/100*7.5, w: (fbp.w??47)/100*13.33, h: 0.45, fontSize:_fb.fontSize, color:_fb.color, bold:_fb.bold, italic:_fb.italic, align:'left' });
    slide.addText(htmlToTextRuns(info.footerWebsite || WEBSITE_URL, { fontSize:_fb.fontSize, color:_fb.color, bold:_fb.bold }), { x: fwp.x/100*13.33, y: fwp.y/100*7.5, w: (fwp.w??23)/100*13.33, h: 0.45, fontSize:_fb.fontSize, color:_fb.color, bold:_fb.bold, align:'center' });
    slide.addText(htmlToTextRuns(info.teacherName || TEACHER_NAME, { fontSize:_fn.fontSize, color:_fn.color, bold:_fn.bold, italic:_fn.italic }), { x: fnp.x/100*13.33, y: fnp.y/100*7.5, w: (fnp.w??24)/100*13.33, h: 0.38, fontSize:_fn.fontSize, color:_fn.color, bold:_fn.bold, italic:_fn.italic });
  };

  const addTopRightLogo = (slide) => {
    if (!logoData) return;
    const lp = info.logoPos ?? { x: 86, y: 0 };
    try { slide.addImage({ data: logoData, x: lp.x/100*13.33, y: lp.y/100*7.5, w: 1.85, h: 1.85, sizing:{ type:'contain' } }); } catch(e){}
  };

  // render free-form elements onto a PPT slide
  // canvasX/canvasW: offset and width of the canvas area within the slide (inches)
  const renderElements = (pptSlide, elements, canvasY, canvasH, canvasX = 0, canvasW = 13.33) => {
    (elements || []).forEach(el => {
      const x = canvasX + (el.x / 100) * canvasW;
      const y = canvasY + (el.y / 100) * canvasH;
      const w = Math.max(0.3, (el.w / 100) * canvasW);
      const h = Math.max(0.15, (el.h / 100) * canvasH);
      if (el.type === 'image' && el.content) {
        try { pptSlide.addImage({ data: el.content, x, y, w, h, sizing:{ type:'contain' } }); } catch(e) {}
      } else if (el.type === 'text' && el.content) {
        const _tb = { fontSize: Math.round((el.fontSize||13) * 72/84), bold: !!el.bold, italic: !!el.italic, color: (el.color||'#111111').replace('#','') };
        pptSlide.addText(htmlToTextRuns(el.content, _tb), { x, y, w, h, ..._tb, underline: !!el.underline,
          align: el.align || 'left', wrap:true, valign:'top',
          fill: el.bgColor ? { color: el.bgColor.replace('#','') } : { type: 'none' } });
      } else if (el.type === 'question') {
        pptSlide.addShape(pres.ShapeType.roundRect, { x, y, w, h, fill:{ color:'EDE9FE' }, rectRadius:0.08, line:{ color:'7C3AED', width:1 } });
        if (el.content) { const _qb = { fontSize: Math.round((el.fontSize||12) * 72/84), bold: el.bold!==false, italic: !!el.italic, color:'5B21B6' }; pptSlide.addText(htmlToTextRuns(el.content, _qb), { x:x+0.1, y:y+0.05, w:w-0.2, h:h-0.1, ..._qb, underline: !!el.underline, wrap:true, valign:'top' }); }
      } else if (el.type === 'shape') {
        const stroke = (el.stroke || '#111111').replace('#','');
        const fill = (el.fill || '#dbeafe').replace('#','');
        const strokeW = el.strokeWidth || 2;
        const kind = el.shapeKind || 'rect';
        if (kind === 'line' || kind === 'dotted') {
          const angle = ((el.rotate || 0) * Math.PI) / 180;
          const cx = x + w / 2, cy = y + h / 2, half = w / 2;
          pptSlide.addShape(pres.ShapeType.line, {
            x1: cx - half * Math.cos(angle),
            y1: cy - half * Math.sin(angle),
            x2: cx + half * Math.cos(angle),
            y2: cy + half * Math.sin(angle),
            line: { color: stroke, pt: strokeW, dash: kind === 'dotted' ? 'dot' : undefined },
          });
        } else if (kind === 'circle') {
          pptSlide.addShape(pres.ShapeType.ellipse, { x, y, w, h, fill:{ color: fill }, line:{ color: stroke, pt: strokeW } });
        } else {
          pptSlide.addShape(pres.ShapeType.rect, { x, y, w, h, fill:{ color: fill }, line:{ color: stroke, pt: strokeW } });
        }
      }
    });
  };

  const totalSlides = 1 + slides.length + (includeSummary ? 1 : 0);
  let slideNum = 0;

  // ── COVER ──
  slideNum++;
  const s1 = pres.addSlide();
  s1.background = { color: (coverBg.bgColor || t.dark).replace('#','') };
  addBackgroundImage(s1, coverBg.image, coverBg.opacity ?? 0.3);
  addTopRightLogo(s1);
  s1.addShape(pres.ShapeType.rect, { x:0,y:0,w:0.53,h:7.5, fill:{ color:t.accent.replace('#','') } });
  const _cbp = info.coverBoxPos ?? { x: 12, y: 14, w: 76 };
  const _bx = _cbp.x / 100 * 13.33, _by = _cbp.y / 100 * 7.5, _bw = _cbp.w / 100 * 13.33;
  // lecture badge — pill shape + dark text on accent background
  const _cn = _pptSt('coverNum', { fontSize:12, color:t.dark, bold:true }); // CSS sp-cover-num clamp max 14px → 12pt
  const _badgeW = 2.0, _badgeH = 0.35;
  s1.addShape(pres.ShapeType.roundRect, { x:_bx, y:_by, w:_badgeW, h:_badgeH, fill:{ color:t.accent.replace('#','') }, rectRadius:0.5, line:{ color:t.accent.replace('#',''), pt:0 } });
  s1.addText(stripHtml(info.coverNum ?? `Lecture ${info.num}`).toUpperCase(), { x:_bx, y:_by, w:_badgeW, h:_badgeH, fontSize:_cn.fontSize, bold:true, color:_cn.color, align:'center', valign:'middle', charSpacing:3 });
  const _ct = _pptSt('coverTopic', { fontSize:48, color:'#FFFFFF', bold:true }); // CSS sp-cover-topic clamp max 56px → 48pt
  s1.addText(htmlToTextRuns(info.coverTopicLine ?? (info.topic || 'Topic'), { fontSize:_ct.fontSize, bold:_ct.bold, italic:_ct.italic, color:_ct.color }), { x:_bx,y:_by+0.62,w:_bw,h:1.5, fontSize:_ct.fontSize, bold:_ct.bold, italic:_ct.italic, color:_ct.color, align:'left' });
  const _cc = _pptSt('coverChapter', { fontSize:15, color:'#C8DCFF' }); // CSS sp-cover-chapter clamp max 18px → 15pt
  s1.addText(htmlToTextRuns(info.coverChapterLine ?? (info.chapter ? `Chapter: ${info.chapter}` : ''), { fontSize:_cc.fontSize, italic:true, color:_cc.color }), { x:_bx,y:_by+2.22,w:_bw,h:0.4, fontSize:_cc.fontSize, italic:true, color:_cc.color, align:'left' });
  // thin divider
  s1.addShape(pres.ShapeType.rect, { x:_bx,y:_by+2.72,w:_bw,h:0.02, fill:{ color:'FFFFFF', transparency:87 } });
  // subtitle tagline
  s1.addText(stripHtml(info.coverSubtitle || 'Complete Physics for JEE/NEET & Class 11-12th').toUpperCase(), { x:_bx,y:_by+2.87,w:_bw,h:0.35, fontSize:11, bold:true, color:t.accent.replace('#',''), align:'left', charSpacing:4 });
  renderElements(s1, info.coverElements||[], 0, 7.5);
  addTeacherBlock(s1);
  addFooter(s1, true);

  // ── CONTENT SLIDES ──
  slides.forEach((slide) => {
    slideNum++;
    if (slide.type === 'concept') {
      const s = pres.addSlide();
      s.background = { color: (slide.bgColor || 'FFFFFF').replace('#','') };
      addBackgroundImage(s, slide.backgroundImage, slide.backgroundOpacity ?? 0.3);
      // headingY = canvas start; 15pt matches CSS clamp max of 20px (20×0.75)
      const headingY = slide.heading ? 0.71 : 0.19;
      if (slide.heading) {
        s.addText(htmlToTextRuns(slide.heading, { fontSize:17, bold:true, color:t.brand.replace('#','') }), { x:0.33,y:0.19,w:10.67,h:0.42, fontSize:17, bold:true, color:t.brand.replace('#','') });
        s.addShape(pres.ShapeType.rect, { x:0.33,y:0.65,w:10.1,h:0.02, fill:{ color:t.accent.replace('#','') } }); // CSS .sp-rule width:80% of content
      }
      addTopRightLogo(s); // drawn after rule so logo is above it (matches CSS z-index)
      renderElements(s, slide.elements, headingY, 6.95 - headingY, 0.4, 12.53);
      addTeacherBlock(s);
      addFooter(s);
    }

    if (slide.type === 'blank') {
      const s = pres.addSlide();
      s.background = { color: (slide.bgColor || 'FFFFFF').replace('#','') };
      addBackgroundImage(s, slide.backgroundImage, slide.backgroundOpacity ?? 0.3);
      addTopRightLogo(s);
      renderElements(s, slide.elements, 0.19, 6.76, 0.4, 12.53);
      addTeacherBlock(s);
      addFooter(s);
    }
  });

  // ── SUMMARY ──
  if (includeSummary) {
    slideNum++;
    const ss = pres.addSlide();
    ss.background = { color: (summaryBg.bgColor || t.dark).replace('#','') };
    addBackgroundImage(ss, summaryBg.image, summaryBg.opacity ?? 0.3);
    const _st = _pptSt('summaryTitle', { fontSize:17, color:'#FFFFFF', bold:true }); // CSS sp-heading clamp max 20px → 17pt
    ss.addText(htmlToTextRuns(info.summaryTitle || 'Chapter Complete ✅  Key Takeaways', { fontSize:_st.fontSize, bold:_st.bold, italic:_st.italic, color:_st.color }), { x:0.33,y:0.19,w:10.67,h:0.42, fontSize:_st.fontSize, bold:_st.bold, italic:_st.italic, color:_st.color });
    ss.addShape(pres.ShapeType.rect, { x:0.33,y:0.65,w:10.1,h:0.02, fill:{ color:t.accent.replace('#','') } }); // CSS .sp-rule width:80%
    addTopRightLogo(ss);
    renderElements(ss, info.summaryElements||[], 0, 7.5);
    addTeacherBlock(ss);
    addFooter(ss, true);
  }

  pres.writeFile({ fileName: `Lecture_${info.num}_${(info.topic||'Slide').replace(/\s+/g,'_')}.pptx` });
}

// ── Element factory ─────────────────────────────────────────────────────────
function makeNewElement(type, payload = '') {
  const id = Date.now() + Math.floor(Math.random() * 999);
  if (type === 'image') return { id, type: 'image', x: 10, y: 10, w: 35, h: 40, content: payload };
  if (type === 'question') return { id, type: 'question', x: 5, y: 5, w: 75, h: 10, content: '', fontSize: 12, bold: true, italic: false, underline: false, color: '#5B21B6' };
  if (type === 'clone') return { ...payload, id, x: Math.min((payload.x ?? 5) + 3, 87), y: Math.min((payload.y ?? 5) + 3, 85) };
  if (type === 'symbol') return { id, type: 'text', x: 10, y: 10, w: 7, h: 9, content: typeof payload === 'string' ? payload : '', fontSize: 22, bold: false, italic: false, underline: false, color: '#111111' };
  if (type === 'shape') {
    const isLine = payload?.shapeKind === 'line' || payload?.shapeKind === 'dotted';
    const isCircle = payload?.shapeKind === 'circle';
    return { id, type: 'shape', x: 10, y: 15,
      w: isLine ? 28 : (isCircle ? 15 : 18),
      h: isLine ? 5  : (isCircle ? 15 : 14),
      shapeKind: payload?.shapeKind || 'rect', fill: '#dbeafe', stroke: '#1e40af', strokeWidth: 2 };
  }
  return { id, type: 'text', x: 5, y: 5, w: 25, h: 8, content: typeof payload === 'string' ? payload : '', fontSize: 16, bold: false, italic: false, underline: false, color: '#111111' };
}

// ── Main Component ────────────────────────────────────────────────────────────
const INITIAL_INFO = { num: '1', topic: '', chapter: '', teacherName: TEACHER_NAME, brandCopy: BRAND_COPY, footerWebsite: WEBSITE_URL, coverTags: 'Complete Physics for JEE/NEET & Class 11-12th', summaryTitle: '✅ Summary', summaryNote: '', showLogo: true, showTeacherBox: true, teacherBoxColor: '#000000', logoPos: null, teacherPos: null, footerNamePos: null, footerBrandPos: null, footerWebsitePos: null, showFooterName: true, showFooterBrand: true, coverNum: null, coverTopicLine: null, coverChapterLine: null, coverBoxPos: { x: 12, y: 14, w: 76 }, coverSubtitle: 'Complete Physics for JEE/NEET & Class 11-12th', coverSubtitlePos: { x: 8, y: 62, w: 84 }, styles: {}, coverElements: [], summaryElements: [] };

export default function SlideCreator() {
  const { currentUser } = useAuth();
  const storageKey = currentUser?.uid ? `sc_draft_${currentUser.uid}` : null;
  const draftLoaded = useRef(false);
  const saveTimer = useRef(null);

  const [info, setInfo] = useState({ ...INITIAL_INFO });
  const [theme, setTheme] = useState('navy');
  const [customColors, setCustomColors] = useState({ brand: '#6D28D9', accent: '#FCD34D' });
  const [slides, setSlides, undoSlides, redoSlides] = useUndoState(defaultSlides);
  const [specialBackgrounds, setSpecialBackgrounds] = useState({
    cover: { image: '', opacity: 0.3 },
    summary: { image: '', opacity: 0.3 },
  });
  const [includeSummary, setIncludeSummary] = useState(true);

  // Restore last draft when user identity is resolved
  useEffect(() => {
    if (!storageKey || draftLoaded.current) return;
    draftLoaded.current = true;
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return;
      const d = JSON.parse(raw);
      if (d.info) setInfo({ ...d.info, footerNamePos: null, footerBrandPos: null, footerWebsitePos: null, teacherPos: null,
        // migrate stale default URLs
        footerWebsite: ['https://prrepxpathshala.live','www.prrepxpathshala.live','🌐 https://prrepxpathshala.live','🌐 prrepxpathshala.live','prrepxpathshala.live'].includes(d.info.footerWebsite) || /^🌐/.test(d.info.footerWebsite || '')
            ? WEBSITE_URL
            : d.info.footerWebsite });
      if (d.slides?.length) setSlides(d.slides);
      if (d.theme) setTheme(d.theme);
      if (d.customColors) setCustomColors(d.customColors);
      if (d.specialBackgrounds) setSpecialBackgrounds(d.specialBackgrounds);
      if (typeof d.includeSummary === 'boolean') setIncludeSummary(d.includeSummary);
    } catch (e) { /* ignore corrupt data */ }
  }, [storageKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-save draft on every change (debounced)
  useEffect(() => {
    if (!storageKey || !draftLoaded.current) return;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      try {
        localStorage.setItem(storageKey, JSON.stringify({ info, slides, theme, customColors, specialBackgrounds, includeSummary }));
      } catch (e) { /* ignore quota errors */ }
    }, 800);
    return () => clearTimeout(saveTimer.current);
  }, [storageKey, info, slides, theme, customColors, specialBackgrounds, includeSummary]); // eslint-disable-line react-hooks/exhaustive-deps
  const [previewIdx, setPreviewIdx] = useState(0); // 0 = cover, 1..n = slides, last = summary
  const [downloading, setDownloading] = useState(false);
  const [showGraphPicker, setShowGraphPicker] = useState(false);
  const [graphCat, setGraphCat] = useState('Algebra');
  const bgFileRef = useRef(null);
  const canvasImgRef = useRef(null);
  const themePickerRef = useRef(null);

  // only one dropdown open at a time; close all on outside click
  useEffect(() => {
    const onToggle = e => {
      if (e.target.tagName !== 'DETAILS' || !e.target.open) return;
      document.querySelectorAll('.sc-page details[open]').forEach(d => {
        if (d !== e.target) d.removeAttribute('open');
      });
    };
    const onDocClick = e => {
      if (!e.target.closest('details')) {
        document.querySelectorAll('.sc-page details[open]').forEach(d => d.removeAttribute('open'));
      }
    };
    document.addEventListener('toggle', onToggle, true);
    document.addEventListener('click', onDocClick);
    return () => {
      document.removeEventListener('toggle', onToggle, true);
      document.removeEventListener('click', onDocClick);
    };
  }, []);

  // global undo/redo — Ctrl+Z / Ctrl+Y (not triggered inside inputs)
  useEffect(() => {
    const onKey = e => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.isContentEditable) return;
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z' && !e.shiftKey) {
        e.preventDefault(); undoSlides();
      } else if ((e.ctrlKey || e.metaKey) && (e.key.toLowerCase() === 'y' || (e.shiftKey && e.key.toLowerCase() === 'z'))) {
        e.preventDefault(); redoSlides();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [undoSlides, redoSlides]);

  const allSlides = ['cover', ...slides, ...(includeSummary ? ['summary'] : [])];
  const totalPreview = allSlides.length;
  const safeIdx = Math.min(previewIdx, totalPreview - 1);

  const addSlide = useCallback((type) => {
    const newSlide = type === 'concept'
      ? { id: Date.now(), type: 'concept', heading: '', elements: [], backgroundImage: '', backgroundOpacity: 0.3, bgColor: '' }
      : { id: Date.now(), type, elements: [], backgroundImage: '', backgroundOpacity: 0.3, bgColor: '' };
    setSlides(s => [...s, newSlide]);
    setPreviewIdx(allSlides.length); // jump to new slide
  }, [allSlides.length]);

  const removeSlide = useCallback((id) => {
    setSlides(s => s.filter(sl => sl.id !== id));
    setPreviewIdx(p => Math.max(0, p - 1));
  }, []);

  const updateSlide = useCallback((id, patch) => {
    setSlides(s => s.map(sl => sl.id === id ? { ...sl, ...patch } : sl));
  }, []);

  const addElement = useCallback((slideId, type, payload = '') => {
    setSlides(s => s.map(sl => sl.id === slideId
      ? { ...sl, elements: [...(sl.elements || []), makeNewElement(type, payload)] } : sl));
  }, []);

  const updateElement = useCallback((slideId, elId, patch) => {
    setSlides(s => s.map(sl => sl.id === slideId
      ? { ...sl, elements: (sl.elements||[]).map(el => el.id === elId ? { ...el, ...patch } : el) } : sl));
  }, []);

  const removeElement = useCallback((slideId, elId) => {
    setSlides(s => s.map(sl => sl.id === slideId
      ? { ...sl, elements: (sl.elements||[]).filter(el => el.id !== elId) } : sl));
  }, []);

  // cover / summary free-form elements (stored in info)
  const addInfoElement = useCallback((key, type, payload = '') => {
    setInfo(i => ({ ...i, [key]: [...(i[key] || []), makeNewElement(type, payload)] }));
  }, []);
  const updateInfoElement = useCallback((key, elId, patch) => {
    setInfo(i => ({ ...i, [key]: (i[key] || []).map(el => el.id === elId ? { ...el, ...patch } : el) }));
  }, []);
  const removeInfoElement = useCallback((key, elId) => {
    setInfo(i => ({ ...i, [key]: (i[key] || []).filter(el => el.id !== elId) }));
  }, []);

  const updatePoint = useCallback((id, idx, val) => {
    setSlides(s => s.map(sl => {
      if (sl.id !== id) return sl;
      const points = [...sl.points];
      points[idx] = val;
      return { ...sl, points };
    }));
  }, []);

  const moveSlide = useCallback((id, dir) => {
    setSlides(s => {
      const i = s.findIndex(sl => sl.id === id);
      const j = i + dir;
      if (j < 0 || j >= s.length) return s;
      const copy = [...s];
      [copy[i], copy[j]] = [copy[j], copy[i]];
      return copy;
    });
  }, []);

  const resolvedTheme = theme === 'custom'
    ? buildCustomTheme(customColors.brand, customColors.accent)
    : THEMES[theme];

  const isCoverView = safeIdx === 0;
  const isSummaryView = includeSummary && safeIdx === allSlides.length - 1;
  const activeEditableSlide = safeIdx > 0 && safeIdx <= slides.length ? slides[safeIdx - 1] : null;
  const activeCanvasAdd = isCoverView
    ? (type, content) => addInfoElement('coverElements', type, content)
    : isSummaryView
    ? (type, content) => addInfoElement('summaryElements', type, content)
    : activeEditableSlide
    ? (type, content) => addElement(activeEditableSlide.id, type, content)
    : null;

  // click-on-blank-canvas → insert text at cursor (only for content slides)
  const handleCanvasClickEmpty = activeEditableSlide
    ? (x, y) => {
        const id = Date.now();
        setSlides(s => s.map(sl => sl.id === activeEditableSlide.id
          ? { ...sl, elements: [...(sl.elements||[]), { id, type:'text', x, y, w:55, h:10, content:'', fontSize:13, bold:false, italic:false, underline:false, color:'#111111' }] }
          : sl));
        return id;
      }
    : null;

  // Ctrl+D duplicate element
  const handleDuplicateElement = isCoverView
    ? elId => setInfo(i => {
        const el = (i.coverElements||[]).find(e => e.id === elId);
        if (!el) return i;
        return { ...i, coverElements: [...i.coverElements, { ...el, id: Date.now(), x: Math.min(el.x+3,87), y: Math.min(el.y+3,85) }] };
      })
    : isSummaryView
    ? elId => setInfo(i => {
        const el = (i.summaryElements||[]).find(e => e.id === elId);
        if (!el) return i;
        return { ...i, summaryElements: [...i.summaryElements, { ...el, id: Date.now(), x: Math.min(el.x+3,87), y: Math.min(el.y+3,85) }] };
      })
    : activeEditableSlide
    ? elId => setSlides(s => s.map(sl => {
        if (sl.id !== activeEditableSlide.id) return sl;
        const el = (sl.elements||[]).find(e => e.id === elId);
        if (!el) return sl;
        return { ...sl, elements: [...sl.elements, { ...el, id: Date.now(), x: Math.min(el.x+3,87), y: Math.min(el.y+3,85) }] };
      }))
    : null;

  // copies one element to every other content slide
  const handleApplyElementToAll = activeEditableSlide
    ? elId => setSlides(s => {
        const src = s.find(sl => sl.id === activeEditableSlide.id);
        const el = (src?.elements || []).find(e => e.id === elId);
        if (!el) return s;
        return s.map(sl => {
          if (sl.id === activeEditableSlide.id) return sl;
          return { ...sl, elements: [...(sl.elements || []), { ...el, id: Date.now() + Math.floor(Math.random() * 9999) }] };
        });
      })
    : null;

  const currentBg = isCoverView
    ? specialBackgrounds.cover
    : isSummaryView
      ? specialBackgrounds.summary
      : activeEditableSlide
        ? { image: activeEditableSlide.backgroundImage || '', opacity: activeEditableSlide.backgroundOpacity ?? 0.3, bgColor: activeEditableSlide.bgColor || '' }
        : { image: '', opacity: 0.3, bgColor: '' };

  const setCurrentBgPatch = useCallback((patch) => {
    if (isCoverView) {
      setSpecialBackgrounds(v => ({ ...v, cover: { ...v.cover, ...patch } }));
      return;
    }
    if (isSummaryView) {
      setSpecialBackgrounds(v => ({ ...v, summary: { ...v.summary, ...patch } }));
      return;
    }
    if (activeEditableSlide) {
      updateSlide(activeEditableSlide.id, {
        backgroundImage: patch.image !== undefined ? patch.image : activeEditableSlide.backgroundImage,
        backgroundOpacity: patch.opacity !== undefined ? patch.opacity : (activeEditableSlide.backgroundOpacity ?? 0.3),
        bgColor: patch.bgColor !== undefined ? patch.bgColor : (activeEditableSlide.bgColor || ''),
      });
    }
  }, [isCoverView, isSummaryView, activeEditableSlide, updateSlide]);

  const onBgPick = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setCurrentBgPatch({ image: ev.target.result });
    reader.readAsDataURL(file);
    e.target.value = '';
  }, [setCurrentBgPatch]);

  const handleReset = () => {
    if (!window.confirm('Reset everything and start fresh? This will clear all slides and your saved draft.')) return;
    if (storageKey) localStorage.removeItem(storageKey);
    setInfo({ ...INITIAL_INFO });
    setSlides(defaultSlides);
    setTheme('navy');
    setCustomColors({ brand: '#6D28D9', accent: '#FCD34D' });
    setSpecialBackgrounds({ cover: { image: '', opacity: 0.3 }, summary: { image: '', opacity: 0.3 } });
    setIncludeSummary(true);
  };

  // pre-render all graph thumbnails when picker opens
  const graphThumbs = useMemo(() => {
    if (!showGraphPicker) return {};
    return Object.fromEntries(GRAPH_DEFS.map(g => [g.id, renderGraph(g, 120)]));
  }, [showGraphPicker]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDownload = async () => {
    setDownloading(true);
    try { await generatePPT({ info, themeObj: resolvedTheme, slides, includeSummary, specialBackgrounds }); }
    catch(e) { console.error(e); }
    finally { setDownloading(false); }
  };

  const t = resolvedTheme;
  const currentBgBadge = currentBg.image ? 'Background ready' : 'No background';

  return (
    <div className="sc-page">
      <SelectionToolbar />
      <div className="sc-header">
        <div className="sc-header__left">
          <h1 className="sc-header__title">🎨 Lecture Slide Creator</h1>
          {true && (
            <div className="sc-header-canvas-btns">
              <button className="sc-header-cbtn" onClick={() => activeCanvasAdd('question')}>❓ Question</button>

              <details className="sc-hdd">
                <summary className="sc-hdd__btn">Shapes ▾</summary>
                <div className="sc-hdd__menu">
                  <button onClick={e => { activeCanvasAdd('shape', { shapeKind: 'rect' }); e.target.closest('details').removeAttribute('open'); }}>▬ Rect</button>
                  <button onClick={e => { activeCanvasAdd('shape', { shapeKind: 'circle' }); e.target.closest('details').removeAttribute('open'); }}>⚫ Circle</button>
                  <button onClick={e => { activeCanvasAdd('shape', { shapeKind: 'line' }); e.target.closest('details').removeAttribute('open'); }}>— Line</button>
                  <button onClick={e => { activeCanvasAdd('shape', { shapeKind: 'dotted' }); e.target.closest('details').removeAttribute('open'); }}>⋯ Dotted</button>
                </div>
              </details>

              <details className="sc-hdd">
                <summary className="sc-hdd__btn">Symbols ▾</summary>
                <div className="sc-hdd__menu sc-hdd__menu--symbols">
                  <div className="sc-sym-group-label">Math</div>
                  {['\u00b1','\u00d7','\u00f7','\u221a','\u221e','\u2211','\u222b','\u03c0','\u03b8','\u03b1','\u03b2','\u03b3','\u0394','\u03bb','\u03bc','\u03c3','\u2264','\u2265','\u2260','\u2248','\u2220','\u00b0','\u221d','\u2234','\u22a5'].map(sym => (
                    <button key={sym} className="sc-sym-btn" onClick={e => { activeCanvasAdd('symbol', sym); e.target.closest('details').removeAttribute('open'); }}>{sym}</button>
                  ))}
                  <div className="sc-sym-group-label">Arrows</div>
                  {['\u2192','\u2190','\u2191','\u2193','\u2194','\u21d2','\u21d0','\u21d4','\u2197','\u2198','\u27f9','\u27fa','\u2b06','\u2b07'].map(sym => (
                    <button key={sym} className="sc-sym-btn" onClick={e => { activeCanvasAdd('symbol', sym); e.target.closest('details').removeAttribute('open'); }}>{sym}</button>
                  ))}
                  <div className="sc-sym-group-label">Marks &amp; Figures</div>
                  {['\u2713','\u2717','\u2714','\u2715','\u2605','\u2606','\u25cf','\u25cb','\u25c6','\u25c7','\u25b2','\u25b6','\u26a1','\u2b50','\ud83d\udccc','\ud83d\udd25'].map(sym => (
                    <button key={sym} className="sc-sym-btn" onClick={e => { activeCanvasAdd('symbol', sym); e.target.closest('details').removeAttribute('open'); }}>{sym}</button>
                  ))}
                </div>
              </details>

              <button className="sc-header-cbtn" onClick={() => canvasImgRef.current?.click()}>🖼 Image</button>
              <button className="sc-header-cbtn" onClick={() => setShowGraphPicker(true)}>📈 Graphs</button>
              <input ref={canvasImgRef} type="file" accept="image/*" style={{ display: 'none' }}
                onChange={e => {
                  const file = e.target.files?.[0]; if (!file) return;
                  const reader = new FileReader();
                  reader.onload = ev => activeCanvasAdd('image', ev.target.result);
                  reader.readAsDataURL(file); e.target.value = '';
                }} />
            </div>
          )}
        </div>
        <button className="sc-reset-btn" onClick={handleReset} title="Clear all slides and start fresh">
          🗑 Reset
        </button>
        <button className="sc-download-btn" onClick={handleDownload} disabled={downloading}>
          {downloading ? '⏳ Generating…' : '⬇ Download PPT'}
        </button>
      </div>

      {showGraphPicker && (
        <div className="sc-graph-modal" onClick={e => { if (e.target.classList.contains('sc-graph-modal')) setShowGraphPicker(false); }}>
          <div className="sc-graph-modal__box">
            <div className="sc-graph-modal__head">
              <div className="sc-graph-modal__headrow">
                <span className="sc-graph-modal__title">📈 Standard Graphs — JEE / NEET</span>
                <button className="sc-graph-modal__close" onClick={() => setShowGraphPicker(false)}>✕</button>
              </div>
              <div className="sc-graph-modal__tabs">
                {GRAPH_CATS.map(cat => (
                  <button key={cat}
                    className={`sc-graph-modal__tab${graphCat===cat?' sc-graph-modal__tab--on':''}`}
                    onClick={() => setGraphCat(cat)}>{cat}</button>
                ))}
              </div>
            </div>
            <div className="sc-graph-modal__grid">
              {GRAPH_DEFS.filter(g => g.cat === graphCat).map(g => (
                <button key={g.id} className="sc-graph-modal__card"
                  disabled={!activeCanvasAdd}
                  onClick={() => { activeCanvasAdd('image', renderGraph(g, 600)); setShowGraphPicker(false); }}>
                  {graphThumbs[g.id] && <img src={graphThumbs[g.id]} alt={g.name} className="sc-graph-modal__img" />}
                  <span className="sc-graph-modal__lbl">{g.name}</span>
                </button>
              ))}
            </div>
            <div className="sc-graph-modal__hint">Click a graph to place it on the current slide — resize/move after placing.</div>
          </div>
        </div>
      )}

      <div className="sc-editor-bar">
        <div className="sc-toolbar-group sc-toolbar-group--content">
          <details className="sc-inline-dd sc-inline-dd--placeholders">
            <summary className="sc-inline-dd__summary-select">
              <span className="sc-inline-label">Placeholders</span>
              <span className="sc-inline-dd__value">Lecture / Topic / Chapter</span>
            </summary>
            <div className="sc-inline-dd__menu sc-inline-dd__menu--fields">
              <label className="sc-mini-field">
                <span>Lecture #</span>
                <input type="text" value={info.num} placeholder="1"
                  onChange={e => setInfo(v => ({ ...v, num: e.target.value }))} />
              </label>
              <label className="sc-mini-field sc-mini-field--wide">
                <span>Topic Name</span>
                <input type="text" value={info.topic} placeholder="e.g. Newton's Laws of Motion"
                  onChange={e => setInfo(v => ({ ...v, topic: e.target.value }))} />
              </label>
              <label className="sc-mini-field sc-mini-field--wide">
                <span>Chapter Name</span>
                <input type="text" value={info.chapter} placeholder="e.g. Laws of Motion"
                  onChange={e => setInfo(v => ({ ...v, chapter: e.target.value }))} />
              </label>
              <label className="sc-mini-field sc-mini-field--wide">
                <span>Teacher Name</span>
                <input type="text" value={info.teacherName || ''} placeholder="e.g. Nipun Sir (B.Tech, IIT Bombay)"
                  onChange={e => setInfo(v => ({ ...v, teacherName: e.target.value }))} />
              </label>
              <label className="sc-mini-field sc-mini-field--wide">
                <span>Brand / Copyright</span>
                <input type="text" value={info.brandCopy || ''} placeholder="© 2026 PrrepX Pathshala"
                  onChange={e => setInfo(v => ({ ...v, brandCopy: e.target.value }))} />
              </label>
            </div>
          </details>

          <details className="sc-theme-picker" ref={themePickerRef}>
            <summary>
              <span className="sc-inline-label">Theme</span>
              <span className="sc-tp-pill">
                <span className="sc-tp-dot" style={{background:resolvedTheme.dark,borderColor:resolvedTheme.accent}} />
                <span>{resolvedTheme.emoji} {resolvedTheme.name}</span>
              </span>
              <span className="sc-tp-chevron">▾</span>
            </summary>
            <div className="sc-theme-picker__menu">
              {THEME_GROUPS.map(g => (
                <div key={g.label} className="sc-tp-group">
                  <div className="sc-tp-group-lbl">{g.label}</div>
                  <div className="sc-tp-row">
                    {g.ids.map(id => {
                      const th = THEMES[id];
                      return (
                        <button key={id}
                          className={`sc-tp-card${theme===id?' sc-tp-card--on':''}`}
                          onClick={() => { setTheme(id); themePickerRef.current?.removeAttribute('open'); }}>
                          <div className="sc-tp-swatch">
                            <div className="sc-tp-sw-body" style={{background:th.dark}} />
                            <div className="sc-tp-sw-stripe" style={{background:th.accent}} />
                          </div>
                          <span className="sc-tp-lbl">{th.emoji} {th.name}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
              <div className="sc-tp-group">
                <div className="sc-tp-group-lbl">🎨 Custom Colors</div>
                <div className="sc-tp-row sc-tp-row--custom">
                  <button className={`sc-tp-card${theme==='custom'?' sc-tp-card--on':''}`}
                    onClick={() => { setTheme('custom'); themePickerRef.current?.removeAttribute('open'); }}>
                    <div className="sc-tp-swatch">
                      <div className="sc-tp-sw-body" style={{background:customColors.brand}} />
                      <div className="sc-tp-sw-stripe" style={{background:customColors.accent}} />
                    </div>
                    <span className="sc-tp-lbl">🎨 My Theme</span>
                  </button>
                  {theme === 'custom' && (
                    <div className="sc-tp-custom-pickers">
                      <label className="sc-mini-color">
                        <span>Primary</span>
                        <input type="color" value={customColors.brand}
                          onChange={e => setCustomColors(c => ({ ...c, brand: e.target.value }))} />
                      </label>
                      <label className="sc-mini-color">
                        <span>Accent</span>
                        <input type="color" value={customColors.accent}
                          onChange={e => setCustomColors(c => ({ ...c, accent: e.target.value }))} />
                      </label>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </details>
        </div>

        <div className="sc-toolbar-group sc-toolbar-group--actions">
          <details className="sc-inline-dd sc-inline-dd--slides">
            <summary className="sc-inline-dd__summary-select">
              <span className="sc-inline-label">Slides</span>
              <span className="sc-inline-dd__value">Choose slide type</span>
            </summary>
            <div className="sc-inline-dd__menu sc-inline-dd__menu--slides">
              {Object.entries(SLIDE_TYPES).map(([type]) => (
                <button key={type} className="sc-add-btn" onClick={() => addSlide(type)}>
                  {type === 'concept' ? '+ Concept' : '+ Blank'}
                </button>
              ))}
              <label className="sc-toggle-row">
                <input type="checkbox" checked={includeSummary} onChange={e => setIncludeSummary(e.target.checked)} />
                Include Summary
              </label>
            </div>
          </details>

          <div className="sc-inline-bg">
            <span className="sc-inline-label">Background</span>
            <label className="sc-sbar-color sc-bg-color-pick" title="Slide background color">
              <span>Color</span>
              <input type="color" value={currentBg.bgColor || '#ffffff'} onChange={e => setCurrentBgPatch({ bgColor: e.target.value })} />
            </label>
            {currentBg.bgColor && (
              <button className="sc-add-btn" onClick={() => setCurrentBgPatch({ bgColor: '' })}>Reset Color</button>
            )}
            <button className="sc-add-btn" onClick={() => bgFileRef.current?.click()}>
              {currentBg.image ? 'Change BG Image' : 'Add BG Image'}
            </button>
            {currentBg.image && (
              <button className="sc-add-btn" onClick={() => setCurrentBgPatch({ image: '' })}>Clear BG</button>
            )}
            <label className="sc-bg-opacity">
              Opacity
              <input
                type="range"
                min={0}
                max={1}
                step={0.05}
                value={currentBg.opacity ?? 0.3}
                onChange={e => setCurrentBgPatch({ opacity: Number(e.target.value) })}
              />
              <span>{Math.round((currentBg.opacity ?? 0.3) * 100)}%</span>
            </label>
            <input ref={bgFileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={onBgPick} />
          </div>
        </div>

      </div>

      <div className="sc-body sc-body--workspace">
        <div className="sc-panel sc-panel--slides">
          <h3 className="sc-rail-title">Slides</h3>
          <div className="sc-slide-rail">
            <div className={`sc-slide-chip sc-slide-chip--fixed${previewIdx === 0 ? ' active' : ''}`} onClick={() => setPreviewIdx(0)}>
              <SlideThumbnail slide="cover" info={info} themeObj={resolvedTheme} specialBackgrounds={specialBackgrounds} />
              <div className="sc-slide-chip__footer">
                <span className="sc-slide-chip__num">1</span>
                <span className="sc-slide-chip__type">Cover</span>
              </div>
            </div>

            {slides.map((sl, i) => (
              <div key={sl.id} className={`sc-slide-chip${previewIdx === i + 1 ? ' active' : ''}`} onClick={() => setPreviewIdx(i + 1)}>
                <div className="sc-slide-chip__thumb-wrap">
                  <SlideThumbnail slide={sl} info={info} themeObj={resolvedTheme} specialBackgrounds={specialBackgrounds} />
                  <div className="sc-slide-chip__actions">
                    <button title="Move up" onClick={e => { e.stopPropagation(); moveSlide(sl.id, -1); }} disabled={i === 0}>↑</button>
                    <button title="Move down" onClick={e => { e.stopPropagation(); moveSlide(sl.id, +1); }} disabled={i === slides.length - 1}>↓</button>
                    <button title="Remove" className="sc-slide-item__del" onClick={e => { e.stopPropagation(); removeSlide(sl.id); }}>✕</button>
                  </div>
                </div>
                <div className="sc-slide-chip__footer">
                  <span className="sc-slide-chip__num">{i + 2}</span>
                  <span className="sc-slide-chip__type">{SLIDE_TYPES[sl.type]?.label.replace(/^\S+\s/, '') ?? sl.type}</span>
                </div>
              </div>
            ))}

            {includeSummary && (
              <div
                className={`sc-slide-chip sc-slide-chip--fixed${previewIdx === allSlides.length - 1 ? ' active' : ''}`}
                onClick={() => setPreviewIdx(allSlides.length - 1)}
              >
                <SlideThumbnail slide="summary" info={info} themeObj={resolvedTheme} specialBackgrounds={specialBackgrounds} />
                <div className="sc-slide-chip__footer">
                  <span className="sc-slide-chip__num">{allSlides.length}</span>
                  <span className="sc-slide-chip__type">Summary</span>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="sc-canvas-pane">
          <div className="sc-preview-wrap">
            <div className="sc-canvas-slide-area">
              <div className="sc-preview-aspect">
                <SlidePreview
                  key={safeIdx}
                  slide={allSlides[safeIdx]}
                  info={info}
                  themeObj={resolvedTheme}
                  slideNum={safeIdx + 1}
                  totalSlides={totalPreview}
                  onInfoUpdate={patch => setInfo(v => ({ ...v, ...patch }))}
                  onUpdate={safeIdx > 0 && safeIdx <= slides.length
                    ? patch => updateSlide(allSlides[safeIdx].id, patch)
                    : null}
                  onUpdatePoint={null}
                  onAddElement={activeCanvasAdd || null}
                  onUpdateElement={
                    isCoverView ? (elId, patch) => updateInfoElement('coverElements', elId, patch) :
                    isSummaryView ? (elId, patch) => updateInfoElement('summaryElements', elId, patch) :
                    safeIdx > 0 && safeIdx <= slides.length ? (elId, patch) => updateElement(allSlides[safeIdx].id, elId, patch) : null
                  }
                  onRemoveElement={
                    isCoverView ? elId => removeInfoElement('coverElements', elId) :
                    isSummaryView ? elId => removeInfoElement('summaryElements', elId) :
                    safeIdx > 0 && safeIdx <= slides.length ? elId => removeElement(allSlides[safeIdx].id, elId) : null
                  }
                  onClickEmpty={handleCanvasClickEmpty}
                  onDuplicateElement={handleDuplicateElement}
                  onApplyToAll={handleApplyElementToAll}
                  specialBackgrounds={specialBackgrounds}
                />
              </div>
            </div>
            <div className="sc-preview-nav">
              <button onClick={() => setPreviewIdx(p => Math.max(0, p-1))} disabled={safeIdx === 0}>← Prev</button>
              <div className="sc-preview-dots">
                {allSlides.map((_, i) => (
                  <button key={i} className={`sc-pdot${i === safeIdx ? ' active' : ''}`}
                    style={i === safeIdx ? { background: t.accent } : {}}
                    onClick={() => setPreviewIdx(i)} />
                ))}
              </div>
              <span className="sc-preview-counter">{safeIdx+1} / {totalPreview}</span>
              <button onClick={() => setPreviewIdx(p => Math.min(totalPreview-1, p+1))} disabled={safeIdx === totalPreview-1}>Next →</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
